// ============================================
// Pinked & Owned — Application Bootstrap
// ES6 module entry point
// ============================================

// Engine modules
import { events, Events } from './engine/events.js';
import { storyManager } from './engine/story.js';
import { fetchStoryJson, renderLoadFailureCard } from './engine/story-fetch.js';
import { variableManager } from './engine/variables.js';
import { tagHandler } from './engine/tags.js';
import { VIEWER_NAMES, getEffectiveFemGain, getEffectiveSubGain, getEffectiveShameGain, getPassiveArousalGain, getArousalPenalty, getComfortDiscount } from './engine/externals.js';
import { isPat } from './engine/pgate.js';

// System modules
import { saveSystem } from './systems/save.js';
import { inventoryManager } from './systems/inventory.js';
import { audioManager } from './systems/audio.js';
import { appearanceState } from './systems/appearance.js';
import { achievementManager } from './systems/achievements.js';
import { statisticsTracker } from './systems/statistics.js';
import { L } from './systems/localization.js';
import { sanitizeName } from './utils/escape.js';

// UI modules — core
import { layoutManager } from './ui/layout.js';
import { statsUI } from './ui/stats.js';
import { storyContent } from './ui/story-content.js';
import { probeImageTar, loadImageTar, loadRestTar, isTarMode, getImageUrl, resolveImage, pinBlobUrl, retryTarNow, getTarStatus } from './image-loader.js';
import { choicesUI } from './ui/choices.js';

// UI modules — features
import { phoneUI } from './ui/phone.js';
import { streamingUI } from './ui/streaming.js';
import { locationsUI, LOCATION_DEFS } from './ui/locations.js';
import { computerUI } from './ui/computer.js';
import { galleryUI } from './ui/gallery.js';
import { shopUI } from './ui/shop.js';
import { wardrobeUI } from './ui/wardrobe.js';
import { characterPanelUI } from './ui/character-panel.js';
import { paperDoll } from './ui/paper-doll.js';
import { settingsUI } from './ui/settings.js';
import { plannerUI } from './ui/planner.js';
import { velvetMinigame } from './ui/velvet-minigame.js';
import { barMinigame } from './ui/bar-minigame.js';
import { resortCocktailMinigame } from './ui/resort-cocktail-minigame.js';
import { cabaretMinigame } from './ui/cabaret-minigame.js';

// UI modules — extracted overlays/panels
import { updateHints } from './ui/hints.js';
import { tutorialTips } from './ui/tutorial-tips.js';
import { historyUI } from './ui/history.js';
import { journalUI } from './ui/journal.js';
import { achievementsOverlayUI } from './ui/achievements-overlay.js';
import { characterEditorUI } from './ui/character-editor.js';
import { saveLoadUI } from './ui/save-load-ui.js';
import { cheatConsoleUI } from './ui/cheat-console.js';
import { undoButtonUI } from './ui/undo-button.js';
import { lovenseSettingsUI } from './ui/lovenseSettings.js';
import { lovenseTriggers } from './systems/lovense-triggers.js';
import { lovenseWidget } from './ui/lovenseWidget.js';
import { loadGameIncremental, registerUndoStateProviders } from './systems/game-loader.js';
import { VERSION_INFO, populateChangelog, checkForUpdates } from './ui/changelog.js';
import { initEntitlementUI } from './ui/entitlement.js';
import { initDesktopUpdate } from './ui/desktop-update.js';
import { initAndroidUpdate } from './ui/android-update.js';
import { creditsUI } from './ui/credits.js';
import { imageVoteService, gameDataService, saveHealthService, setVersionInfoGetter } from './services/analytics.js';
import { cloudSaveManager } from './systems/cloud-save.js';

// Editor data (still needed for test hooks: window._pno_PERKS)
import { PERKS, PERK_CATEGORIES } from './ui/editor-data.js';

// ============================================
// Global media-element registry (hypno audio kill-switch)
// ============================================
// Tracks EVERY <video>/<audio> ever created this session — including the
// _expandVideo MODAL COPY and any variant clone that the per-surface teardowns
// (detachHypnoVideo only touches _hypnoVideoEl; _releaseVideos / sweeps only
// scan #story-content; _hypnoVideoRegistry only holds _addVideo clips) never
// reach. Root cause of the recurring "hypno audio won't stop, invisible in
// every log" loop (foundby:owner 0.2.8.6 Firefox): a DETACHED `loop` <video>
// (e.g. the modal copy after the modal closed) keeps emitting its native audio
// track — querySelectorAll can't see detached nodes, the `paused` flag still
// reads true, and FF152 ignores pause() on it. The only reliable stop is a full
// UNLOAD (src removed + load()) on the element itself, so we must be able to
// find it. This global Set + kill makes that possible.
const __allMedia = new Set();
(function _patchMediaCreate() {
    try {
        const _ce = document.createElement.bind(document);
        document.createElement = function (tag) {
            const el = _ce(tag);
            try { if (typeof tag === 'string' && /^(video|audio)$/i.test(tag)) __allMedia.add(el); } catch (e) {}
            return el;
        };
    } catch (e) { /* createElement not patchable — degrade gracefully */ }
})();
// Kill every hypno clip that is NOT the live in-#story-content beat. Idempotent.
function killDetachedHypnoMedia() {
    for (const v of [...__allMedia]) {
        try {
            const src = ((v.currentSrc || (v.getAttribute && v.getAttribute('src')) || v.src || '') + '');
            if (!/hypno|hmcat/i.test(src)) {
                if (!v.isConnected) __allMedia.delete(v);   // prune dead non-hypno entries
                continue;
            }
            // Keep ONLY a hypno clip still mounted in the live story container
            // (the current beat). Everything else — detached orphans, the modal
            // copy in #image-modal, scrollback clips — gets fully unloaded.
            if (v.isConnected && typeof v.closest === 'function' && v.closest('#story-content')) continue;
            v.muted = true;
            v.loop = false;
            v.pause();
            v.removeAttribute('src');
            v.load();   // mechanism-independent: a src-less element makes ZERO sound even if FF152 ignored pause()
            __allMedia.delete(v);
        } catch (e) { __allMedia.delete(v); }
    }
}
// Debug hooks (always on — cheap): owner can confirm the source from the console.
window.__listMedia = () => [...__allMedia].map(v => {
    try { return { src: ((v.currentSrc || v.src || '') + '').split('/').pop(), paused: v.paused, muted: v.muted, loop: v.loop, inDOM: v.isConnected }; }
    catch (e) { return { err: 1 }; }
}).filter(x => x.src);
window.__killHypnoAudio = () => { killDetachedHypnoMedia(); return 'killed detached hypno media'; };
// Fire on every scene leave (STORY_STOP_MEDIA = refreshView-top). Pairs with
// audioManager.detachHypnoVideo (handles the single gain-routed _hypnoVideoEl).
events.on(Events.STORY_STOP_MEDIA, () => { try { killDetachedHypnoMedia(); } catch (e) {} });

// ============================================
// Global Error Handler + Debug Flag
// ============================================
// Debug mode: localStorage.setItem('PnO_Debug', 'true') or ?debug in URL
const PNO_DEBUG = localStorage.getItem('PnO_Debug') === 'true' || location.search.includes('debug');
const ENDSCREEN_KEYS = new Set(['kept', 'independent', 'famous', 'masc', 'kept_her']);
window.PNO_DEBUG = PNO_DEBUG;

// Suppress verbose tagged logs in production (keep warn/error untouched)
if (!PNO_DEBUG) {
    const _origLog = console.log;
    console.log = function() {
        if (typeof arguments[0] === 'string' && arguments[0].charAt(0) === '[') return;
        _origLog.apply(console, arguments);
    };
}

// Console log capture — ring buffer for bug reports
const _consoleLog = { buffer: [], MAX: 50 };
(function() {
    const orig = { warn: console.warn, error: console.error };
    function capture(level, args) {
        const msg = Array.from(args).map(a => {
            if (a instanceof Error) return a.message;
            if (typeof a === 'object') try { return JSON.stringify(a).substring(0, 200); } catch(e) { return String(a); }
            return String(a);
        }).join(' ').substring(0, 300);
        _consoleLog.buffer.push({ t: Date.now(), l: level, m: msg });
        if (_consoleLog.buffer.length > _consoleLog.MAX) _consoleLog.buffer.shift();
    }
    console.warn = function() { capture('W', arguments); orig.warn.apply(console, arguments); };
    console.error = function() { capture('E', arguments); orig.error.apply(console, arguments); };
})();
window._consoleLog = _consoleLog;

// ============================================
// Format an Error/event payload into a single dense line that survives the
// 300-char console capture limit. Includes filename:line:col and the first
// 2 stack frames so bug reports include attribution (most "Cannot read
// properties of null" reports were unattributable before this — see
// 0.2.5.7.2 "Images bugged" report).
function _formatErrorForBugReport(err, e) {
    const msg = err?.message || e?.message || String(err || '(no error)');
    // Strip leading "https://host/path/" off filename so the basename:line:col fits
    let loc = '';
    if (e?.filename) {
        const fn = e.filename.split('/').pop().split('?')[0];
        loc = ` @ ${fn}:${e.lineno || '?'}:${e.colno || '?'}`;
    }
    let stack = '';
    if (err?.stack) {
        // Keep first 2 frames after the message line, normalise URLs to basenames
        const frames = String(err.stack).split('\n').slice(1, 3)
            .map(f => f.trim().replace(/https?:\/\/[^\s)]*\//g, ''))
            .join(' | ');
        if (frames) stack = ` || ${frames}`;
    }
    return msg + loc + stack;
}

window.addEventListener('error', (e) => {
    console.error('[PnO] Uncaught error:', _formatErrorForBugReport(e.error, e));
});
window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const formatted = (reason instanceof Error)
        ? _formatErrorForBugReport(reason, null)
        : String(reason);
    console.error('[PnO] Unhandled promise rejection:', formatted);
});

// ============================================
// Application State
// ============================================
const App = {
    story: null,
    initialized: false,
    characterData: null,
};

// Shared opts for loadGameIncremental calls (needs App + characterPanelUI)
function _loaderOpts() {
    return {
        setCharacterData: (d) => { App.characterData = d; },
        updateCharacterPanelSlots: () => characterPanelUI.updateSlots(),
    };
}

/**
 * Boot the Ink story from scratch, apply a save onto it, wire collectors + UI,
 * and switch to the game view. Used by main-menu Continue AND main-menu
 * slot-load — both start with NO story booted, so calling loadGameIncremental
 * directly fails with "no story". In-game loads (pause overlay) already have a
 * story and patch via loadGameIncremental directly, so they skip this.
 * foundby:VPS — [VPS] Broken Saves 0.2.7: main-menu slot-load hit "no story".
 */
async function bootAndApplySave(saveData) {
    const storyJson = await fetchStoryJson();

    storyManager.initStory(storyJson);
    choicesUI.setChoiceHandler((index) => storyManager.selectChoice(index));
    appearanceState.registerInkReader((v) => {
        try { return storyManager.currentStory?.variablesState[v]; } catch(e) { return undefined; }
    });
    shopUI.setStoryReader(() => storyManager.currentStory);

    if (saveData.characterData) {
        App.characterData = saveData.characterData;
    }

    // Starter items first (awaited so the optional item-DB recovery fetch
    // completes before loadGameIncremental restores saved equipment).
    await inventoryManager.giveStarterItems();
    // Inventory pusher: OBSERVED equipment-stats route through setBuffedVar;
    // BOOLEAN flags write directly to story.variablesState (not OBSERVED, so
    // setFlag would pollute variableManager.flags — rule #1 save-state-drift).
    inventoryManager.registerInkPusher((name, value) => {
        if (typeof value === 'boolean') {
            // Route boolean flags through setFlag so the JS flag mirror
            // (variableManager.flags) is updated alongside the Ink var. A raw
            // `story.variablesState[name] = ...` write with observers blocked
            // sets Ink but NEVER updates the mirror — getFlag()/save serialise
            // from the mirror, so equipment-derived flags desynced when changed
            // via the wardrobe overlay (no story refresh after to run
            // syncFromInk). Symptom: cage equipped via wardrobe → wearing_chastity
            // stayed false → nude view + panel showed the dick uncaged.
            // Foundby:VPS player v0.2.7.4 ("Dick is locked but there is no
            // related image"). See save-state-drift.md.
            variableManager.setFlag(name, value);
        } else if (typeof value === 'string') {
            // String vars (chastity_form "cage"/"belt"/"") are Ink-only
            // narrative state. setBuffedVar is numeric-only — its base+value
            // fallback concats 0 + 'belt' into '0belt' and clobbers correct
            // Ink-side writes. Non-observed string → direct Ink write only
            // (save-state-drift rule #1).
            try {
                const story = storyManager.currentStory;
                if (story) story.variablesState[name] = value;
            } catch (e) { console.warn('[Inventory] string push failed:', name, e?.message); }
        } else {
            variableManager.setBuffedVar(name, value);
        }
    });
    inventoryManager.registerInkReader((name) => {
        const story = storyManager.currentStory;
        if (!story) return 0;
        try { return story.variablesState[name]; } catch(e) { return 0; }
    });

    loadGameIncremental(saveData, _loaderOpts());

    saveSystem.registerCollectors({
        getStory: () => storyManager.currentStory || null,
        getVariables: () => variableManager.exportState(),
        getInventory: () => inventoryManager.getSerializableState(),
        getCharacterData: () => App.characterData,
        getSMS: () => phoneUI.serialize?.()?.threads || [],
        getStatistics: () => statisticsTracker.getSerializableState(),
        getAchievements: () => achievementManager.getUnlockedIds?.() || [],
        getGalleryUnlocked: () => galleryUI.serialize?.() || [],
        getCurrentLocation: () => storyManager.currentLocation || '',
        gameVersion: VERSION_INFO.fullVersion,
    });

    registerUndoStateProviders();

    // Register shop items + inventory ref so save-loaded items show as Owned
    // (ITEM_ADDED only fires on live add; restoreFromSave bypasses it)
    shopUI.registerItems(inventoryManager.getAllItems());
    shopUI.setInventoryRef(inventoryManager);
    phoneUI.setStoryRef(storyManager.currentStory);
    phoneUI.setStoryGetter(() => storyManager.currentStory);

    wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
    wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
    wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
    characterPanelUI.updateSlots();

    events.emit(Events.SCREEN_CHANGE, 'screen-game-view');
}

// ============================================
// Initialization
// ============================================
// ── Loading screen helpers ─────────────────────────────────────────────
let _loadingMax = 0;
function _loadingProgress(p, label) {
    if (p < _loadingMax) return; // never go backwards
    _loadingMax = p;
    const fill = document.getElementById('loading-fill');
    const pct = document.getElementById('loading-pct');
    const labelEl = document.getElementById('loading-label');
    if (fill) fill.style.width = Math.round(p * 100) + '%';
    if (pct) pct.textContent = Math.round(p * 100) + '%';
    if (labelEl && label) labelEl.textContent = label;
}
function _loadingDone() {
    const overlay = document.getElementById('loading-overlay');
    if (!overlay) return;

    // Show mood splash on first visit (or if never dismissed)
    const hasSeenSplash = localStorage.getItem('PnO_SplashSeen');
    if (!hasSeenSplash) {
        // Fade out loading, then show splash
        const fadeOutLoading = () => {
            if (typeof gsap !== 'undefined') {
                gsap.to(overlay, { opacity: 0, duration: 0.5, ease: 'power2.inOut', onComplete: () => {
                    overlay.remove();
                    _showMoodSplash();
                }});
            } else {
                overlay.style.opacity = '0';
                setTimeout(() => { overlay.remove(); _showMoodSplash(); }, 500);
            }
        };
        fadeOutLoading();
        return;
    }

    // Returning player: skip splash, go straight to menu
    if (typeof gsap !== 'undefined') {
        gsap.to(overlay, { opacity: 0, duration: 0.6, ease: 'power2.inOut', onComplete: () => overlay.remove() });
    } else {
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 600);
    }
}

function _showMoodSplash() {
    const splash = document.getElementById('mood-splash');
    if (!splash) return;

    splash.classList.remove('hidden');
    splash.setAttribute('aria-hidden', 'false');

    // Play splash voiceover. Scale by the user's saved SFX setting so a
    // previously-muted player isn't surprised by audio on first boot of a
    // new install. Player report @Brendo: sliders set to 0 didn't mute the
    // splash because the volume was hardcoded.
    try {
        const sfxScale = Math.max(0, Math.min(1, audioManager.sfxVolume));
        const splashVoice = new Howl({
            // Howler picks the first playable source. Ogg is the disk asset;
            // keep .wav as a fallback for older web_backup bundles.
            src: ['assets/audio/sfx/splash_voice_leda.ogg', 'assets/audio/sfx/splash_voice_leda.wav'],
            volume: 0.7 * sfxScale,
            html5: true
        });
        splashVoice.play();
    } catch(e) {}

    const SPLASH_DURATION = 3000; // 3 seconds (was 5s, first-time intro polish)
    let dismissed = false;

    function dismiss() {
        if (dismissed) return;
        dismissed = true;
        localStorage.setItem('PnO_SplashSeen', '1');

        if (typeof gsap !== 'undefined') {
            gsap.to(splash, { opacity: 0, duration: 0.8, ease: 'power2.inOut', onComplete: () => {
                splash.remove();
            }});
        } else {
            splash.style.opacity = '0';
            setTimeout(() => splash.remove(), 800);
        }
    }

    // Click anywhere to skip
    splash.addEventListener('click', dismiss);
    // Auto-dismiss after 5 seconds
    setTimeout(dismiss, SPLASH_DURATION);
}

// ── Global image interceptor for tar mode (itch.io builds) ────────────
// When images.tar is loaded, intercepts all <img> and background-image
// references and replaces them with blob URLs from the tar archive.

const IMG_PATH_RE = /assets\/images\/([^/]+)\/([^/.]+)\.(png|jpg|jpeg|webp)/i;

function startImageInterceptor() {
    if (!isTarMode()) return;
    console.log('[PnO] Image interceptor active (tar mode)');

    // Initial full pass. Repeated once after the core tar and once after the
    // rest tar land (see the loadImageTar/loadRestTar chains) — getImageUrl
    // returns null for anything the archive doesn't hold yet, so elements
    // examined too early stay unmarked until a full pass re-visits them.
    _interceptAllImages();

    // Incremental observer. The old shape re-ran _interceptAllImages() on
    // EVERY childList mutation: three full-document walks (img scan, [style]
    // scan, every-CSS-rule-in-every-sheet scan) per typing character, stat
    // floater and choice render. Worse, [style] elements without a
    // background-image never earn the data-tar-bg mark, so that candidate
    // set grew monotonically — per-mutation cost scaled with total session
    // DOM. Symptom: performance degrades over a session, reload is a temp
    // fix (player report 2026-07-02). Now only the added subtrees and
    // src/style attribute flips are examined (cost bounded by the mutation,
    // not the document); the CSS-rule walk runs only in the full passes.
    // F26: keep a reference on window so test-harness / hot-reload paths can
    // disconnect. Production singleton lives session-long, expected.
    const observer = new MutationObserver((muts) => {
        for (const mut of muts) {
            if (mut.type === 'attributes') {
                _interceptElement(mut.target);
                continue;
            }
            for (const node of mut.addedNodes) {
                if (node.nodeType !== 1) continue;   // elements only
                _interceptElement(node);
                if (node.querySelectorAll) {
                    node.querySelectorAll('img, [style]').forEach(el => _interceptElement(el));
                }
            }
        }
    });
    observer.observe(document.body, {
        childList: true, subtree: true,
        attributes: true, attributeFilter: ['src', 'style'],
    });
    window._imageInterceptorObserver = observer;
}

// One element, O(1): swap a raw assets/images/ <img src> or inline
// background-image for its tar blob URL. Mirrors the two full-scan branches
// in _interceptAllImages — keep them in sync. Our own blob-URL writes
// re-trigger the observer's src/style filter, but the data-tar marks (and
// the blob: prefix checks) short-circuit them immediately — no loop.
function _interceptElement(el) {
    try {
        if (el.tagName === 'IMG' && !el.hasAttribute('data-tar')) {
            const src = el.getAttribute('src') || '';
            if (src.includes('assets/images/') && !src.startsWith('blob:')) {
                const match = src.match(IMG_PATH_RE);
                const blobUrl = match ? getImageUrl(match[2]) : null;
                if (blobUrl) {
                    el.src = blobUrl;
                    el.setAttribute('data-tar', '1');
                }
            }
        }
        if (el.hasAttribute && el.hasAttribute('style') && !el.hasAttribute('data-tar-bg')) {
            const bg = (el.style && el.style.backgroundImage) || '';
            if (bg.includes('assets/images/') && !bg.includes('blob:')) {
                const match = bg.match(IMG_PATH_RE);
                const blobUrl = match ? getImageUrl(match[2]) : null;
                if (blobUrl) {
                    el.style.backgroundImage = `url(${blobUrl})`;
                    el.setAttribute('data-tar-bg', '1');
                }
            }
        }
    } catch (e) { /* detached/foreign node — skip */ }
}

function _interceptAllImages() {
    // 1. Replace <img src="assets/images/..."> with blob URLs
    document.querySelectorAll('img:not([data-tar])').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (!src.includes('assets/images/') || src.startsWith('blob:')) return;
        const match = src.match(IMG_PATH_RE);
        if (!match) return;
        const blobUrl = getImageUrl(match[2]);
        if (blobUrl) {
            img.src = blobUrl;
            img.setAttribute('data-tar', '1');
        }
    });

    // 2. Replace background-image: url(assets/images/...) with blob URLs
    document.querySelectorAll('[style]:not([data-tar-bg])').forEach(el => {
        const bg = el.style.backgroundImage || '';
        if (!bg.includes('assets/images/') || bg.includes('blob:')) return;
        const match = bg.match(IMG_PATH_RE);
        if (!match) return;
        const blobUrl = getImageUrl(match[2]);
        if (blobUrl) {
            el.style.backgroundImage = `url(${blobUrl})`;
            el.setAttribute('data-tar-bg', '1');
        }
    });

    // 3. Rewrite CSS-stylesheet `url(assets/images/...)` rules in-place.
    // Inline-style branch (#2) only catches `style="..."` attrs, not rules
    // declared in linked .css files. CSS-rule url() that 404s leaves
    // narrator_ornament.png / similar UI decorations blank even in tar mode
    // (F11 from 2026-05-26 playthrough audit).
    _interceptCssRuleUrls();
}

function _interceptCssRuleUrls() {
    // No once-flag: tar may not be loaded yet on first call (getImageUrl
    // returns null pre-load). MutationObserver retries on every DOM change;
    // the `cur.includes('blob:')` skip below makes follow-up calls cheap
    // because already-replaced rules short-circuit.
    const sheets = Array.from(document.styleSheets || []);
    for (const sheet of sheets) {
        let rules;
        try { rules = sheet.cssRules; } catch (e) { continue; } // cross-origin
        if (!rules) continue;
        for (const rule of rules) {
            if (!rule.style) continue;
            const props = ['backgroundImage', 'background'];
            for (const p of props) {
                const cur = rule.style[p];
                if (!cur || !cur.includes('assets/images/') || cur.includes('blob:')) continue;
                const match = cur.match(IMG_PATH_RE);
                if (!match) continue;
                const blobUrl = getImageUrl(match[2]);
                if (!blobUrl) continue;
                // CSSOM references are invisible to the eviction-revoke
                // reference check — pin so the rule's blob survives LRU.
                pinBlobUrl(blobUrl);
                rule.style[p] = cur.replace(/url\([^)]*assets\/images\/[^)]+\)/, `url(${blobUrl})`);
            }
        }
    }
}


// Boot checkpoint logger — records each await hop so we can trace
// where a silent init hang lands. Accessible as window._bootTrace for
// post-mortem reads when the loading screen sticks. Low noise: fires
// ~15 times total during boot.
const _bootTrace = [];
function _bootCheck(stage) {
    const t = Date.now();
    const prev = _bootTrace[_bootTrace.length - 1];
    const dt = prev ? t - prev.t : 0;
    _bootTrace.push({ stage, t, dt });
    console.log(`[PnO boot] ${stage} +${dt}ms`);
}
if (typeof window !== 'undefined') window._bootTrace = _bootTrace;

// Blocking, self-contained "core data failed to load" overlay. Used when the
// item database (and usually en.tsv / story) 404s persistently on the CDN edge,
// which otherwise soft-locks the player at an empty wardrobe. Hard-coded EN +
// inline styles on purpose: L10n and the stylesheet may be the very things that
// failed to load, so this must not depend on game assets.
function _showDataLoadError() {
    try {
        if (document.getElementById('pno-dataload-error')) return;
        const el = document.createElement('div');
        el.id = 'pno-dataload-error';
        el.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;background:rgba(20,12,28,0.97);color:#f3e9f5;font-family:system-ui,-apple-system,sans-serif;text-align:center;padding:24px;box-sizing:border-box';
        el.innerHTML =
            '<div style="font-size:42px">&#9888;&#65039;</div>' +
            '<div style="font-size:20px;font-weight:700">Game data could not be loaded</div>' +
            '<div style="max-width:440px;line-height:1.55;opacity:0.9">Some core files (clothing, text) failed to download. This is almost always a temporary network or CDN hiccup, not a problem with your save. Please reload the page, and check your connection.</div>' +
            '<button id="pno-dataload-reload" style="margin-top:6px;padding:12px 30px;font-size:16px;font-weight:700;border:0;border-radius:10px;background:#d3366e;color:#fff;cursor:pointer">Reload</button>';
        document.body.appendChild(el);
        const btn = document.getElementById('pno-dataload-reload');
        if (btn) btn.addEventListener('click', () => location.reload());
    } catch (e) {
        // Last resort: if even the DOM overlay throws, a reload is the only path.
        console.error('[PnO] data-load error overlay failed:', e?.message);
    }
}

// Audit D26: body-class taxonomy. Was ad-hoc `window.innerWidth < 768`
// scattered across 8+ JS sites with no `.layout-desktop` ever applied at
// 1920+. Single source of truth — re-evaluates on resize.
function _applyLayoutBodyClass() {
    const w = window.innerWidth;
    const cls = document.body.classList;
    cls.toggle('layout-mobile', w < 768);
    cls.toggle('layout-tablet', w >= 768 && w < 1024);
    cls.toggle('layout-desktop', w >= 1024);
    cls.toggle('layout-wide', w >= 1440);
    cls.toggle('layout-orientation-portrait', window.innerHeight > window.innerWidth);
    cls.toggle('layout-orientation-landscape', window.innerWidth >= window.innerHeight);
}

async function init() {
    console.log('[PnO] Initializing...');
    _bootCheck('init-start');
    _loadingProgress(0.05, 'Starting up...');

    // D26: install body-class taxonomy before any UI module reads it.
    _applyLayoutBodyClass();
    let _layoutResizeT;
    const _reflowAndApply = () => {
        _applyLayoutBodyClass();
        // Force a layout pass so env(safe-area-inset-*) and 100dvh re-resolve.
        // Android 16 Capacitor 8 WebView in edge-to-edge mode (targetSdk=36)
        // updates the visual viewport when system bars hide/show but doesn't
        // always invalidate cached safe-area values until something triggers
        // reflow — content stays at the pre-toggle size until the next click.
        void document.body.offsetHeight;
    };
    window.addEventListener('resize', () => {
        clearTimeout(_layoutResizeT);
        _layoutResizeT = setTimeout(_reflowAndApply, 120);
    });
    window.addEventListener('orientationchange', _reflowAndApply);
    // Android WebView fires visualViewport.resize when system bars toggle
    // even if window.resize doesn't. Anonymous Pixel 9 Pro XL / Android 16
    // bug report 2026-05-14: "screen cut off until next click" — exactly
    // this class. Debounce shared with window.resize.
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
            clearTimeout(_layoutResizeT);
            _layoutResizeT = setTimeout(_reflowAndApply, 120);
        });
    }

    // §76: the edge-to-edge viewport can be stale on first paint (content cut
    // off until the player's first interaction, foundby:killer2005de in MuMu +
    // the Pixel 9 report). No resize event fires when it is stale from the
    // start, so force a reflow once the first screen renders, plus one more
    // after a beat for slow WebViews. Idempotent / no-op where already correct.
    events.once(Events.SCREEN_CHANGE, () => {
        requestAnimationFrame(_reflowAndApply);
        setTimeout(_reflowAndApply, 600);
    });

    // 0. Check if images.tar exists (itch.io builds)
    _bootCheck('probe-tar:start');
    const hasTar = await probeImageTar();
    _bootCheck('probe-tar:done');
    if (hasTar) {
        _loadingProgress(0.08, 'Checking image archive...');
        // Kick the big download (~90-160MB) WITHOUT awaiting — the menu
        // becomes interactive while it streams (first boot on cellular used
        // to block the menu for minutes behind this single await). Game
        // entry gates on _ensureImagesReady(); _loadingProgress is monotonic
        // so the concurrent tar callbacks can't fight the later boot steps.
        // Cache-API warm boots resolve this promise in well under a second.
        _bootCheck('load-tar:start');
        loadImageTar((p) => {
            _loadingProgress(0.08 + p * 0.42, 'Loading images...');
        }).then(() => {
            _bootCheck('load-tar:done');
            // Core tar landed after the interceptor's initial pass may have
            // run against an empty archive — the incremental observer no
            // longer retries the whole document per mutation (leak fix
            // 2026-07-02), so run one explicit full pass now.
            try { _interceptAllImages(); } catch (e) { /* interceptor not started yet — its own initial pass covers this */ }
            // Core tar is in; now stream the heavy REST tar in the background
            // (best-effort, never gates play — split-tar 0.2.8.1.2). Kicked
            // after core so the two big downloads don't contend on mobile.
            loadRestTar().then(() => {
                // Same reason: elements referencing rest-tar-only images were
                // skipped (getImageUrl null) until now.
                try { _interceptAllImages(); } catch (e) { /* non-fatal */ }
            }).catch((e) => console.warn('[PnO] Rest tar load failed (core intact):', e?.message || e));
        }).catch((e) => console.warn('[PnO] Tar boot load failed (recovery handles it):', e?.message || e));
    } else {
        _loadingProgress(0.08, 'Checking image archive...');
    }

    // 0b. Initialize save system (migrate localStorage → IndexedDB)
    _bootCheck('save-init:start');
    await saveSystem.init();
    _bootCheck('save-init:done');
    _loadingProgress(0.55, 'Initializing save system...');

    try {
    // 1. Initialize UI systems
    layoutManager.init();
    statsUI.init();
    statsUI.setVarAccessor(name => variableManager.getFlag(name) || variableManager.getValue(name));
    locationsUI.setStoryAccessor(() => storyManager.currentStory);
    locationsUI.setStoryManager(storyManager);
    storyContent.init();
    choicesUI.init();
    undoButtonUI.init();
    // Lovense trigger bus — Patreon-gated + toggle-gated inside the handler,
    // safe to bind unconditionally. No-op until user enables + pairs a toy.
    lovenseTriggers.bind();
    lovenseWidget.init();
    } catch (e) { console.error('[PnO] UI init failed:', e); }
    _bootCheck('ui-init:done');

    _loadingProgress(0.60, 'Building interface...');

    // 1a. Load localization, video manifest, and item database in parallel.
    // Individual failures caught per-promise so one stall doesn't block the rest.
    _loadingProgress(0.62, 'Loading data...');
    _bootCheck('data-init:start');
    await Promise.all([
        L.init().then(() => _bootCheck('data:L.init')).catch(e => { _bootCheck('data:L.init-FAIL'); console.warn('[PnO] L10n init failed:', e); }),
        appearanceState.loadVideoManifest().then(() => _bootCheck('data:video-manifest')).catch(e => { _bootCheck('data:video-manifest-FAIL'); console.warn('[PnO] Video manifest failed:', e); }),
        inventoryManager.loadItemDatabase().then(() => { _bootCheck('data:item-db'); console.log('[PnO] Item database loaded'); }).catch(e => { _bootCheck('data:item-db-FAIL'); console.warn('[PnO] Failed to load item database:', e); }),
    ]);
    _bootCheck('data-init:done');
    // Resilience: a persistent item-DB load failure (CDN / network 404) leaves
    // the wardrobe empty and soft-locks the player at "put on clothes to leave"
    // with nothing to equip. One more retry, then a clear reload prompt instead
    // of a silent empty-wardrobe dead-end. (foundby: Android Free 0.2.9.0.3,
    // items.json 404 on the itch CDN edge; en.tsv + story 404'd too.)
    if (inventoryManager._itemDb.size === 0) {
        console.warn('[PnO] Item DB empty after boot load, final retry before alerting');
        try { await inventoryManager.loadItemDatabase(); } catch (e) { /* handled below */ }
        if (inventoryManager._itemDb.size === 0) {
            _bootCheck('data:item-db-FATAL');
            _showDataLoadError();
        }
    }
    // Wire L10n variable interpolation to read Ink vars at runtime
    L.setVariableResolver((varName) => variableManager.getInkVariable(varName));
    L.registerFunction('get_viewer_name', () => VIEWER_NAMES[Math.floor(Math.random() * VIEWER_NAMES.length)]);
    // display_name() — name NPCs call the PC by. Mirrors the Ink function
    // (Globals.ink) for the L10n TSV-value interpolation path: feminine name once
    // feminized (Fem>=50), else the masculine name (empty fem name or masc route).
    L.registerFunction('display_name', () => {
        const giv = (v) => variableManager.getInkVariable(v);
        const fem = giv('FeminineName') || '';
        const player = giv('PlayerName') || '';
        if (!fem) return player;
        if (Number(giv('masc_path') || 0) >= 1) return player;
        return (Number(giv('Femininity') || 0) >= 50) ? fem : player;
    });
    _loadingProgress(0.80, 'Loading data...');

    // 2. Load saved character data
    try {
        const saved = localStorage.getItem('PnO_CharacterData');
        if (saved) {
            App.characterData = JSON.parse(saved);
        }
    } catch (e) {
        console.warn('[PnO] Failed to load character data:', e);
    }

    // 3. Wire up main menu buttons
    bindMainMenu();
    // Tar failure banner (Retry button) — listens for pno-tar-state events.
    _wireTarStateBanner();

    // 3a. Show language popup on first visit. The popup chains into the
    // combined Welcome modal (18+ verification + analytics opt-in +
    // image-vote opt-in) once the user picks a language, so first launch
    // is a single sequence: language → welcome.
    settingsUI.showLanguagePopup();

    // 3a2. Returning user with language already chosen but missing 18+
    // confirmation or analytics consent — go straight to the welcome
    // modal. No-op when both prefs are already saved.
    if (localStorage.getItem('PnO_LanguageChosen')) {
        settingsUI.showWelcomeModal();
    }

    // 3b. Bind tutorial tip triggers
    tutorialTips.bindTriggers();

    // 3c. Wire planner exit callback
    plannerUI.setAutoSelectCallback(tutorialTips.autoSelectExitChoice);

    // 4. Wire up character editor (extracted module)
    characterEditorUI.init({
        setCharacterData: (d) => { App.characterData = d; },
        getCharacterData: () => App.characterData,
        updateCharacterPanelSlots: () => characterPanelUI.updateSlots(),
        getVersionInfo: () => VERSION_INFO,
    });
    characterEditorUI.bind();

    // 4b. Wire up save/load UI (extracted module)
    saveLoadUI.init({
        setCharacterData: (d) => { App.characterData = d; },
        loaderOpts: _loaderOpts(),
    });

    // 5. Wire up game view controls
    bindGameControls();

    // 6. (Age verification now lives inside the combined Welcome modal —
    // see settings.js showWelcomeModalIfNeeded. Nothing to do here.)

    // 7. Check for Continue save
    updateContinueButton();

    // 8. Wire up cross-system event listeners
    bindSystemEvents();

    _loadingProgress(0.85, 'Binding events...');

    // 9. Populate changelog and version + show popup for returning players
    try { populateChangelog(); } catch (e) { console.error('[PnO] populateChangelog failed:', e); }
    try {
        const lastSeen = localStorage.getItem('PnO_LastSeenVersion');
        if (lastSeen && lastSeen !== VERSION_INFO.version) {
            // Returning player with new version — auto-expand What's New
            const sidebar = document.querySelector('.menu-sidebar');
            if (sidebar) sidebar.scrollIntoView({ behavior: 'smooth' });
            // Flash the What's New section
            const whatsNew = document.getElementById('whats-new-list');
            if (whatsNew) {
                whatsNew.style.animation = 'none';
                requestAnimationFrame(() => {
                    whatsNew.style.animation = 'screen-fade-in 0.6s ease both';
                    whatsNew.style.boxShadow = '0 0 20px rgba(228, 140, 184, 0.2)';
                    setTimeout(() => { whatsNew.style.boxShadow = ''; }, 3000);
                });
            }
        }
        localStorage.setItem('PnO_LastSeenVersion', VERSION_INFO.version);
    } catch (e) { console.warn('[PnO] LastSeenVersion / whatsNew banner failed:', e?.message); }

    // 10. Initialize overlay content
    journalUI.init();
    achievementsOverlayUI.init();
    historyUI.init();
    settingsUI.initToggles();
    settingsUI.initMuteToggles();
    settingsUI.initContentPrefsPause();
    settingsUI.initArousalBreathing();
    cheatConsoleUI.init();
    setVersionInfoGetter(() => VERSION_INFO);
    imageVoteService.init();
    gameDataService.init();
    saveHealthService.init();

    // Cloud Save Sync — init the manager (re-derives key from stored code,
    // restores enabled state). Self-no-ops when the kill-switch is off. On a
    // clean boot, if sync is already enabled + has a code, fire a background
    // pullList() to warm the slot-badge cache. NO blocking modal at boot in
    // v1 — remote-newer hints surface in the save/load overlay only.
    try {
        if (cloudSaveManager.isAvailable()) {
            cloudSaveManager.init();
            if (cloudSaveManager.isEnabled() && cloudSaveManager.hasCode()) {
                // Background, non-blocking — keeps boot clean. Materialize
                // fills EMPTY local slots from the cloud (pulls the list
                // internally), so cross-device saves appear as normal slots.
                setTimeout(() => {
                    cloudSaveManager.materializeSlots()
                        .then(() => { try { saveLoadUI.refreshCloudList?.(true); } catch (e) {} })
                        .catch(e => console.warn('[PnO] cloud boot pull failed:', e?.message));
                }, 1500);
            }
        }
    } catch (e) { console.warn('[PnO] cloud-save init failed:', e?.message); }

    // Cloud-sync settings UI requests a badge re-list after adopt-code /
    // delete-cloud actions (fired with Events.CLOUD_SYNC_LIST_REFRESH, which
    // falls back to the 'cloud:listRefresh' channel). Force-refresh the cached
    // list + re-render slot badges.
    events.on(Events.CLOUD_SYNC_LIST_REFRESH || 'cloud:listRefresh', () => {
        // Slot rows first (a materialize may have just filled an empty slot
        // with a downloaded save), then the badge layer on top.
        try { saveLoadUI.refreshSlots?.(); } catch (e) {}
        try { saveLoadUI.refreshCloudList?.(true); } catch (e) {}
    });

    // 11. UI scale + font size are now owned entirely by the unified
    // PnO_TextScale system in settings.js (bindSliders → migrate +
    // _firstRunAutoScale + _applyPersistedTextScale, already run via
    // bindMainMenu() above). The old width-only auto-detect that lived here
    // applied zoom 1.2 to every 1920px-wide display (incl. 1920x1080 @ DPR1),
    // which pushed the game-view to 120dvh and clipped the bottom of the left
    // column off-screen (phone/wardrobe unreachable, no scroll). foundby:CutieLvr
    // 0.2.7.3. Drop the zombie legacy keys so nothing resurrects the bad zoom.
    try {
        localStorage.removeItem('PnO_UIScale');
        localStorage.removeItem('PnO_FontSize');
    } catch (e) { /* non-fatal */ }

    App.initialized = true;

    // Patreon supporter achievement (idempotent — unlock() no-ops if already unlocked)
    if (isPat()) {
        achievementManager.unlock('patron');
    }

    // Expose for testing/debugging (used by test scripts in iframes)
    window._sm = storyManager;
    window._inv = inventoryManager;
    window._vars = variableManager;
    window._locations = locationsUI;
    window._am = achievementManager;
    window._statsUI = statsUI;
    window._choicesUI = choicesUI;
    window._Events = Events;
    window._save = saveSystem;
    // Test hook: regression specs need to invoke loadGameIncremental against
    // the bundled module graph, not a fresh dynamic import (which would
    // create a second variableManager singleton disconnected from window._vars).
    window._loadGameIncremental = loadGameIncremental;
    // Test hook: setup helpers drive the programmatic quick start through this
    // since the main-menu Quick Start button was removed (0.3.1).
    window._charEditor = characterEditorUI;
    window._cloudSaveManager = cloudSaveManager; // cloud-save-sync.spec.js S6
    window._storyContent = storyContent; // image-modal-video-teardown.spec.js
    window._paperDoll = paperDoll; // paper-doll.spec.js
    // Paper doll: dressed preview in panel + wardrobe (PAPER_DOLL.md Phase 3).
    // Static mounts, render via equip/axis events on both game-start paths.
    paperDoll.mount(document.getElementById('paper-doll-panel'));
    paperDoll.mount(document.getElementById('wardrobe-doll'));
    // Gemerkten Panel-Tab anwenden (Default: clothed = Paper-Doll)
    characterPanelUI.restorePanelMode();
    // Combined side-panel tab (Char / Stats / Hints — default: char)
    characterPanelUI.restoreSidePanelTab();
    window._pno_PERKS = PERKS;
    window._pno_PERK_CATEGORIES = PERK_CATEGORIES;
    window._pno_resolveImage = (folderOrName, name) => {
        // 2-arg: resolveImage(folder, name) — used by shop.js, phone.js
        if (name !== undefined) return resolveImage(folderOrName, name);
        // 1-arg: resolveImage(name) — manifest-based lookup (used by tests
        // + the velvet brothel minigame, which has top-level images).
        const n = folderOrName;
        if (isTarMode()) return getImageUrl(n) || null;
        const manifest = window._imageManifest;
        if (manifest && manifest[n] !== undefined) {
            const folder = manifest[n];
            // Top-level images have folder="" in the manifest — skip the
            // subfolder segment so we don't produce `assets/images//name.png`.
            // D10/D11 fix: resolve to .png here (not .webp). This 1-arg path is
            // loose-mode only (tar mode returned above via getImageUrl), and
            // shipped loose trees carry .png — UI icons have NO loose .webp at
            // all, so the old .webp form returned a dead URL with no fallback
            // (broken phone/app icons). This now matches image-loader.js
            // resolveImage(), which also builds .png in loose mode.
            return folder
                ? `assets/images/${folder}/${n}.png`
                : `assets/images/${n}.png`;
        }
        return null;
    };
    window._events = events;
    window._appearance = appearanceState;
    window._audio = audioManager;
    window._tags = tagHandler;
    window._layout = layoutManager;
    window._L = L;
    window._phone = phoneUI;
    window._shop = shopUI;
    window._wardrobe = wardrobeUI;
    window._streaming = streamingUI;
    window._computer = computerUI;
    window._settingsUI = settingsUI;
    window._gallery = galleryUI;
    window._externals = {
        getEffectiveFemGain: (base) => getEffectiveFemGain(base, storyManager.currentStory),
        getEffectiveSubGain: (base) => getEffectiveSubGain(base, storyManager.currentStory),
        getEffectiveShameGain: (base) => getEffectiveShameGain(base, storyManager.currentStory),
        getPassiveArousalGain: () => getPassiveArousalGain(storyManager.currentStory),
        getArousalPenalty: () => getArousalPenalty(storyManager.currentStory),
        getComfortDiscount: (cost) => getComfortDiscount(cost, storyManager.currentStory),
    };
    window._locationDefs = LOCATION_DEFS;

    // Pacing-debug hooks (PnO_Debug power-user). Console-only — no UI.
    // Usage:
    //   _pno_pacing.set(0/1/2/3)        Slow/Standard/Fast/VerySlow
    //   _pno_pacing.bypass(true|false)  toggle speed_signal_active
    //   _pno_pacing.day(N)              jump Day forward
    //   _pno_pacing.stat('Fem', N)      set Femininity (or any base stat)
    //   _pno_pacing.show()              dump current pacing state
    window._pno_pacing = {
        set(mode) {
            const story = storyManager.currentStory;
            if (!story) return console.warn('[pacing] no story');
            const m = parseInt(mode, 10);
            if (![0, 1, 2, 3].includes(m)) return console.warn('[pacing] mode must be 0/1/2/3');
            try { story.variablesState['pacing_mode'] = m; } catch(e) { console.warn('[pacing]', e); }
            const labels = ['Slow Burn (6W)', 'Standard (5W)', 'Fast Track (4W = old)', 'Very Slow (7W)'];
            console.log(`[pacing] mode set to ${m} = ${labels[m]}`);
        },
        bypass(on) {
            const story = storyManager.currentStory;
            if (!story) return console.warn('[pacing] no story');
            try { story.variablesState['speed_signal_active'] = !!on; } catch(e) { console.warn('[pacing]', e); }
            console.log(`[pacing] speed_signal_active = ${!!on}`);
        },
        day(n) {
            const story = storyManager.currentStory;
            if (!story) return console.warn('[pacing] no story');
            const d = parseInt(n, 10);
            if (isNaN(d) || d < 1) return console.warn('[pacing] day must be >=1');
            try { story.variablesState['Day'] = d; } catch(e) { console.warn('[pacing]', e); }
            try { variableManager.baseVar?.set('Day', d); } catch(e) {}
            try { variableManager.setValue('Day', d); } catch(e) {}
            console.log(`[pacing] Day = ${d}`);
        },
        stat(name, value) {
            const story = storyManager.currentStory;
            if (!story) return console.warn('[pacing] no story');
            const v = parseInt(value, 10);
            if (isNaN(v)) return console.warn('[pacing] value must be a number');
            try { story.variablesState[name] = v; } catch(e) { console.warn('[pacing]', e); }
            try { variableManager.setValue(name, v); } catch(e) {}
            console.log(`[pacing] ${name} = ${v}`);
        },
        show() {
            const story = storyManager.currentStory;
            if (!story) return console.warn('[pacing] no story');
            const get = (k) => { try { return story.variablesState[k]; } catch(e) { return '?'; } };
            const labels = ['Slow Burn (6W)', 'Standard (5W)', 'Fast Track (4W = old)', 'Very Slow (7W)'];
            const m = get('pacing_mode');
            console.table({
                pacing_mode: `${m} = ${labels[m] || '?'}`,
                speed_signal_active: get('speed_signal_active'),
                Day: get('Day'),
                last_major_event_day: get('last_major_event_day'),
                Femininity: get('Femininity'),
                Submissiveness: get('Submissiveness'),
                Shame: get('Shame'),
                MasterRelationship: get('MasterRelationship'),
            });
        },
    };

    window._pno_ready = true;

    // Boot-into-New-Game: the endscreen "New Game (with perks!)" button reloads
    // the page (the menu/editor DOM is unmounted on game-view entry — see
    // layout.js showScreen) and leaves this flag so the fresh boot walks
    // straight into the character editor instead of stopping at the menu.
    // foundby:Monki 0.2.8.6.
    try {
        if (sessionStorage.getItem('PnO_BootIntoNewGame') === '1') {
            sessionStorage.removeItem('PnO_BootIntoNewGame');
            // Drive the normal New Game flow (onboarding gate + image-ready wait +
            // character editor) via a synthetic click. NOT a bare setTimeout(0):
            // on a slow/lagging boot the menu may not be settled when the tick
            // fires — bindMainMenu's #btn-new-game handler unbound, the loading
            // overlay still up, or the main-menu screen mid-transition — and a
            // click into that half-initialized state could land the player in an
            // editor whose name inputs weren't interactive until the next manual
            // interaction (foundby:Inciteful 0.2.9.0.2 "can't change names, fixed
            // itself after leaving + coming back / lag spike"). Gate on readiness:
            // button present + main-menu the active screen + document complete,
            // and let a couple of frames pass so the menu layout has settled.
            let _bootTries = 0;
            const _bootIntoNewGame = () => {
                const btn = document.getElementById('btn-new-game');
                const menu = document.getElementById('screen-main-menu');
                const ready = btn && document.contains(btn)
                    && menu && menu.classList.contains('active') && !menu.dataset.unmounted
                    && document.readyState === 'complete' && window._pno_ready === true;
                if (ready && _bootTries >= 2) { btn.click(); return; }   // settled for ≥2 frames
                if (_bootTries++ < 90) { requestAnimationFrame(_bootIntoNewGame); return; }  // ~1.5s budget
                if (btn) btn.click();   // last resort: try anyway rather than strand on the menu
            };
            requestAnimationFrame(_bootIntoNewGame);
        }
    } catch (e) { /* sessionStorage unavailable — stay on the menu */ }

    // First-time UI tooltips — show once per element type, never again
    _initFirstTimeTooltips();

    // Start image interceptor for bundled mode (replaces img src with data URLs)
    startImageInterceptor();

    _loadingProgress(1, 'Ready!');
    _loadingDone();
    console.log('[PnO] Initialized. Device:', layoutManager.deviceClass);

    // Check for save loss after UI is ready (shows dialog if detected)
    setTimeout(() => saveSystem.checkSaveLoss(), 1500);

    // Telemetry: send session_start with save_count for aggregate tracking
    // This is the primary signal for storage wipes (run before user can delete saves)
    setTimeout(() => saveHealthService.sendSessionStart(saveSystem.getSaveCount()), 2000);

    // Check for app updates (Android/Electron only, silent on web)
    setTimeout(checkForUpdates, 3000);

    // Supporter-update pairing UI in Settings (Tauri desktop only; no-op elsewhere)
    try { initEntitlementUI(); } catch (e) { console.warn('[Entitlement] init failed:', e?.message || e); }

    // In-game OTA update banner (Tauri desktop only; no-op elsewhere)
    try { initDesktopUpdate(); } catch (e) { console.warn('[DesktopUpdate] init failed:', e?.message || e); }

    // In-app APK self-update banner (Android shell only; no-op elsewhere)
    try { initAndroidUpdate(); } catch (e) { console.warn('[AndroidUpdate] init failed:', e?.message || e); }

    // E-07 v0.2.5: synchronous last-resort save on tab-close. The async
    // autoSave's IDB write may not land before the tab dies; this writes
    // the latest collector snapshot to localStorage synchronously so the
    // run survives unexpected closes. Next-session load watchdog
    // (save.js:146-164) re-syncs LS → IDB.
    window.addEventListener('beforeunload', () => saveSystem.flushPendingSync());
}

// ============================================
// First-Time UI Tooltips
// ============================================
const FIRST_TIME_TIPS = [
    { selector: '.stat-row[data-stat="energy"]', key: 'tip_stats', l10nKey: 'tip.stats', text: 'Your stats. Energy lets you act, Arousal builds during encounters, Presentation tracks your transformation.' },
    { selector: '.stat-row[data-stat="submissiveness"]', key: 'tip_sub', l10nKey: 'tip.sub', text: 'Submissiveness changes how NPCs treat you and can lock you out of resistance choices.' },
    { selector: '.stat-row.money-row', key: 'tip_money', l10nKey: 'tip.money', text: 'Money for clothes, upgrades, and services. Earn it through jobs and encounters.' },
    { selector: '.location-map', key: 'tip_map', l10nKey: 'tip.map', text: 'Click locations on the map to explore. New areas unlock as you progress.' },
    { selector: '#right-column', key: 'tip_equip', l10nKey: 'tip.equip', text: 'Your appearance. Equipment affects stats and how others see you.' },
];

function _initFirstTimeTooltips() {
    const seen = JSON.parse(localStorage.getItem('PnO_SeenTips') || '{}');
    const allSeen = FIRST_TIME_TIPS.every(t => seen[t.key]);
    if (allSeen) return; // All tips already shown, skip entirely

    const _checkTips = () => {
        for (const tip of FIRST_TIME_TIPS) {
            if (seen[tip.key]) continue;
            const el = document.querySelector(tip.selector);
            if (!el || !el.offsetParent) continue;
            seen[tip.key] = true;
            localStorage.setItem('PnO_SeenTips', JSON.stringify(seen));
            const localText = tip.l10nKey ? L.get(tip.l10nKey) : null;
            const displayText = (localText && localText !== tip.l10nKey) ? localText : tip.text;
            _showFirstTimeTip(el, displayText);
            break; // Show one tip at a time
        }
        // Stop listening once all shown
        if (FIRST_TIME_TIPS.every(t => seen[t.key])) {
            events.off(Events.STORY_CHOICES_READY, _checkTips);
            events.off(Events.STORY_CONTENT_FINISHED, _checkTips);
        }
    };

    // Check on game state changes instead of every DOM mutation
    events.on(Events.STORY_CHOICES_READY, _checkTips);
    events.on(Events.STORY_CONTENT_FINISHED, _checkTips);
    // Initial check after a short delay
    setTimeout(_checkTips, 2000);
}

function _showFirstTimeTip(anchor, text) {
    const tip = document.createElement('div');
    tip.className = 'first-time-tip';
    const textSpan = document.createElement('span');
    textSpan.className = 'first-time-tip-text';
    textSpan.textContent = text;
    const closeBtn = document.createElement('button');
    closeBtn.className = 'first-time-tip-close';
    closeBtn.textContent = 'OK';
    tip.appendChild(textSpan);
    tip.appendChild(closeBtn);
    document.body.appendChild(tip);

    const rect = anchor.getBoundingClientRect();
    tip.style.left = `${Math.min(rect.right + 12, window.innerWidth - 280)}px`;
    tip.style.top = `${Math.max(rect.top, 8)}px`;

    // Mobile: position below instead
    if (window.innerWidth < 768) {
        tip.style.left = '8px';
        tip.style.right = '8px';
        tip.style.top = `${rect.bottom + 8}px`;
    }

    closeBtn.addEventListener('click', () => {
        tip.classList.add('fade-out');
        setTimeout(() => tip.remove(), 400);
    }, { once: true });

    // Auto-dismiss after 8s
    setTimeout(() => {
        if (tip.parentNode) {
            tip.classList.add('fade-out');
            setTimeout(() => tip.remove(), 400);
        }
    }, 8000);
}

// ============================================
// Save Loss Warning Dialog
// ============================================
function _showSaveLossDialog(lastDate) {
    const overlay = document.createElement('div');
    overlay.className = 'overlay save-loss-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:600;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';

    const box = document.createElement('div');
    box.className = 'overlay-box';
    box.style.cssText = 'max-width:480px;padding:24px;border:1px solid var(--rose);border-radius:12px;background:var(--bg-warm);';

    const title = document.createElement('h3');
    title.style.cssText = 'color:var(--rose);font-family:"Crimson Pro",serif;margin:0 0 12px;';
    title.textContent = L.get('main.saves_gone.title', 'Your saves are gone');

    const body = document.createElement('p');
    body.style.cssText = 'color:var(--text-body);font-size:14px;line-height:1.6;margin:0 0 16px;';
    body.textContent = L.get('main.saves_gone.body', 'It looks like you played on {0}, but your save data has been wiped. This usually happens on iOS Safari due to Intelligent Tracking Prevention, or in private browsing mode. To prevent this, please use Export Save regularly to back up your progress.').replace('{0}', String(lastDate));

    const btn = document.createElement('button');
    btn.className = 'btn-primary';
    btn.textContent = L.get('main.saves_gone.got_it', 'Got it');
    btn.style.cssText = 'padding:10px 24px;background:var(--rose);color:#000;border:none;border-radius:6px;cursor:pointer;font-weight:600;';
    btn.addEventListener('click', () => overlay.remove());

    box.appendChild(title);
    box.appendChild(body);
    box.appendChild(btn);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
}

// ============================================
// Cross-System Event Wiring
// ============================================
function bindSystemEvents() {
    // Save failure notifications (loud, not silent)
    events.on(Events.SAVE_FAILED, ({ slot }) => {
        const where = slot === -1 ? 'Auto-save' : `Slot ${slot + 1}`;
        events.emit(Events.TOAST_REQUEST, `${where} did not save. Use Export Save to keep your run safe.`, 'toast-error');
    });
    events.on(Events.SAVE_FALLBACK_FAILED, ({ silent }) => {
        if (silent) {
            // Auto-save fallback failed. Single failures can be transient
            // (quota fluctuation), but repeated silent failures = user
            // thinks they're saving but nothing persists. Surface after 3.
            window._silentSaveFails = (window._silentSaveFails || 0) + 1;
            if (window._silentSaveFails === 3) {
                events.emit(Events.TOAST_REQUEST,
                    'Auto-save is not persisting in this browser. Export a save before closing the tab.',
                    'toast-warn');
            }
            return;
        }
        events.emit(Events.TOAST_REQUEST, "Browser storage is almost full. Export a save backup before it's gone.", 'toast-warn');
    });
    // Successful save resets the silent-fail accumulator so we don't toast
    // for old transient failures after recovery.
    events.on(Events.SAVE_COMPLETED, () => { window._silentSaveFails = 0; });

    // Probable-private-mode banner (one-time per session). LS flag dedupes
    // re-firing after dismissal in NORMAL mode; in private mode LS is wiped
    // on close so the banner returns each session, which is intended.
    events.on(Events.SAVE_PRIVATE_MODE, ({ quota }) => {
        try {
            if (localStorage.getItem('PnO_PrivateModeAck') === 'true') return;
        } catch(e) {}
        const mb = Math.round(quota / 1024 / 1024);
        const banner = document.createElement('div');
        banner.id = 'private-mode-banner';
        banner.style.cssText = 'position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:99998;background:#2a1a3a;color:#ffd6b8;padding:10px 16px;border-radius:8px;border:1px solid rgba(255,170,120,0.5);font:12px/1.4 system-ui,sans-serif;max-width:520px;text-align:center;box-shadow:0 4px 16px rgba(0,0,0,0.4);';
        banner.innerHTML = `<div style="font-weight:700;margin-bottom:4px;color:#ffaa78">⚠️ Private Browsing detected (${mb}MB quota)</div><div>Your saves will be lost when you close this tab. Use <b>Export Save</b> in Settings to back up your run.</div><button id="private-mode-dismiss" style="margin-top:8px;padding:4px 14px;background:rgba(255,170,120,0.2);border:1px solid rgba(255,170,120,0.4);border-radius:4px;color:#ffd6b8;cursor:pointer;font-size:11px">Got it</button>`;
        document.body.appendChild(banner);
        document.getElementById('private-mode-dismiss')?.addEventListener('click', () => {
            try { localStorage.setItem('PnO_PrivateModeAck', 'true'); } catch(e) {}
            banner.remove();
        });
    });
    // Save loss warning: detected at init when user played before but no saves found
    events.on(Events.SAVE_LOSS_WARNING, ({ lastPlayed }) => {
        const lastDate = lastPlayed ? new Date(lastPlayed).toLocaleDateString() : 'recently';
        _showSaveLossDialog(lastDate);
    });

    // Backup reminder at Day milestones (5, 10, 20, 30, 50)
    // and special iOS Safari warning on first save
    events.on(Events.STAT_CHANGED, (name, newVal, oldVal) => {
        if (name !== 'Day' || newVal <= oldVal) return;
        const milestones = [5, 10, 20, 30, 50];
        if (milestones.includes(newVal)) {
            const seen = JSON.parse(localStorage.getItem('PnO_BackupReminders') || '{}');
            if (seen[newVal]) return;
            seen[newVal] = true;
            try { localStorage.setItem('PnO_BackupReminders', JSON.stringify(seen)); } catch(e) {}
            // iOS gets stronger warning on first milestone
            const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
            const msg = isIOS && newVal === 5
                ? `Day ${newVal}. iOS Safari sometimes wipes browser saves on its own. Settings, Export Save keeps your run safe.`
                : `Day ${newVal}. Good time to export a save backup, just in case.`;
            events.emit(Events.TOAST_REQUEST, msg, 'toast-info');
        }
    });

    // Set-bonus story trigger (Studio Review Task #12). When inventory.js detects
    // a set is newly complete it emits SET_BONUS_ACTIVATED. Map the inkFlag to
    // the corresponding mirror-beat knot in SetBonus.ink and start it as a
    // forced event. The Ink knot itself gates on set_<name>_seen so the beat
    // fires once even after re-equip cycles.
    const SET_BONUS_KNOT = {
        'set_full_lingerie': 'SetBonus_Lingerie',
        'set_maid_service':  'SetBonus_Maid',
        'set_neko':          'SetBonus_Neko',
        'set_office_ready':  'SetBonus_Office',
        'set_bunny_girl':    'SetBonus_Bunny',
        'set_cozy_femme':    'SetBonus_Cozy',
        'set_dark_doll':     'SetBonus_DarkDoll',
        // 0.3 intimate piercings (INTIMATE_PIERCINGS.md §4)
        'set_private_commitment': 'SetBonus_PrivateCommitment',
    };
    events.on(Events.SET_BONUS_ACTIVATED, ({ inkFlag }) => {
        const knot = SET_BONUS_KNOT[inkFlag];
        if (!knot) return;
        // Close any open wardrobe/shop so the mirror beat is actually visible.
        // Wardrobe + shop both fire SET_BONUS_ACTIVATED indirectly (equip / buy),
        // and the resulting forced event would render behind the still-open
        // overlay. layoutManager.closeAllOverlays() in story.js handles this
        // defensively too, but close here matches phone.js + Plug_FirstEquip
        // explicit pattern.
        try { wardrobeUI.close?.(); } catch (e) {}
        _enqueueForcedEvent(knot, 'SetBonus');
    });

    // Wardrobe equip/unequip (wardrobe emits tag + slotIndex)
    events.on('wardrobe:equip', (tag) => {
        // Masculine outer clothing is locked above the Fem cap. Re-checked here
        // (not just on the disabled button) because the preset-apply path
        // re-emits wardrobe:equip and would otherwise bypass the button.
        // Story-forced equips use TAG_ITEM → equipByTag directly, so they skip this.
        if (inventoryManager.isMascClothingFemLocked(tag)) {
            console.log(`[Wardrobe] '${tag}' equip blocked — Femininity past the masc-clothing cap`);
            return;
        }
        // A keyholder/permanent-locked cage must not be displaced by equipping
        // another item into its slot — equipByTag auto-unequips the occupied
        // slot, which bypassed the lock the unequip path enforces (deep-review
        // P1-9: belt over locked cage). Same check the preset path uses.
        try {
            const slot = inventoryManager.getItemByTag(tag)?.slot;
            // getEquippedMap values are item OBJECTS (or null), keyed by slot
            const occupantTag = slot ? inventoryManager.getEquippedMap().get(slot)?.tag : null;
            // Allow swapping among the Master's downsize cage tiers (regular →
            // pink → flat) even when locked — the player stays caged. Still blocks
            // belt/non-cage from displacing a locked cage (P1-9). Without this the
            // Master-ordered pink-cage downsize softlocks (foundby:VPS 0.2.8.3).
            if (occupantTag && occupantTag !== tag && inventoryManager.isCageKeyholderLocked(occupantTag)
                && !inventoryManager.isCageDownsizeTier(tag)) {
                console.log(`[Wardrobe] '${tag}' equip blocked — '${occupantTag}' is keyholder-locked`);
                return;
            }
        } catch (e) { console.warn('[Wardrobe] keyholder-lock check failed:', e?.message); }
        inventoryManager.equipByTag(tag);
        inventoryManager.recalculateAllBuffs();
        // Refresh wardrobe view
        wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
        wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
        wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
    });
    events.on('wardrobe:unequip', (tag) => {
        // CHASTITY_REMOVAL (0.2.8): a keyholder-locked cage can't be taken off
        // via the wardrobe. Re-checked here (not just on the disabled button)
        // because the preset-apply path re-emits wardrobe:unequip and would
        // otherwise bypass the button. Story-forced removals use TAG_ITEM →
        // unequipByTag directly, so they skip this.
        if (inventoryManager.isCageKeyholderLocked(tag)) {
            console.log(`[Wardrobe] '${tag}' unequip blocked — the keyholder holds the key`);
            return;
        }
        inventoryManager.unequipByTag(tag);
        inventoryManager.recalculateAllBuffs();
        // Refresh wardrobe view
        wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
        wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
        wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
    });
    events.on('wardrobe:loadPreset', (items) => {
        // CHASTITY_REMOVAL (0.2.8): a keyholder-locked cage survives preset-apply.
        // Note which slots hold one so we neither unequip them nor equip a preset
        // item that would auto-displace them (equipByTag unequips the same slot).
        const lockedSlots = new Set();
        for (const [slot, equippedItem] of inventoryManager.getEquippedMap()) {
            // getEquippedMap values are item OBJECTS (or null), keyed by slot
            const occupantTag = equippedItem?.tag;
            if (occupantTag && inventoryManager.isCageKeyholderLocked(occupantTag)) { lockedSlots.add(slot); continue; }
            inventoryManager.unequipSlot(slot);
        }
        // Equip preset items, skipping any that target a locked-cage slot.
        for (const tag of Object.values(items)) {
            if (!tag) continue;
            const presetSlot = inventoryManager.getItemByTag?.(tag)?.slot;
            if (presetSlot && lockedSlots.has(presetSlot)) continue;
            inventoryManager.equipByTag(tag);
        }
        // Refresh wardrobe view
        wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
        wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
        wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
    });

    // Shop purchase (inventory items — clothing, accessories)
    events.on('shop:purchase', ({ tag, price }) => {
        const money = variableManager.getTotalValue('Money');
        if (money >= price) {
            variableManager.setValue('Money', money - price);
            inventoryManager.addItemByTag(tag);
            events.emit(Events.AUDIO_SFX_PLAY, 'coins1');
            events.emit(Events.ITEM_PURCHASED, tag);
            const purchasedItem = inventoryManager.getItemByTag?.(tag);
            if (purchasedItem?.displayName) events.emit(Events.TOAST_REQUEST, `You bought the ${purchasedItem.displayName}.`, 'toast-info');

            // Apply purchase side-effects from item data (declarative, no manual switch/case)
            // Sets BOTH Ink story state AND variableManager (so pushVariablesToInk won't overwrite)
            const item = inventoryManager.getItemByTag?.(tag);
            if (item?.setsOnPurchase && storyManager.currentStory) {
                const story = storyManager.currentStory;
                variableManager.blockObservers = true;
                let touchedDecor = false;
                try {
                    // Write directly to story.variablesState only — the 22
                    // setsOnPurchase keys (taking_hormones, chastity_unlocked,
                    // owns_*, dildo_tier, plug_tier) are non-observed Ink-only
                    // VARs. Mirroring them into variableManager.flags / baseVar
                    // pollutes the JS shadow with entries that re-push on
                    // reload and resurrect narrative-cleared state (e.g.
                    // taking_hormones cleared by Clinic prescription expiry
                    // would resurface on save reload). See save-state-drift.md
                    // rule #1. (Audit 2026-05-23.)
                    for (const [varName, value] of Object.entries(item.setsOnPurchase)) {
                        try {
                            story.variablesState[varName] = value;
                            if (varName.startsWith('decor_')) touchedDecor = true;
                        } catch (e) { console.warn(`[Shop] setsOnPurchase '${varName}' failed:`, e?.message); }
                    }
                } finally { variableManager.blockObservers = false; }
                // Decor purchases drive apartment_nest_tier; recompute immediately
                // so the next render uses the new tier (vs waiting for overnight).
                // Also invalidate appearance cache so apartment_* image variants
                // pick up the new tier suffix on next refresh.
                if (touchedDecor) {
                    try { variableManager.evalSafe?.('recompute_nest_tier', []); }
                    catch (e) { console.warn('[Shop] recompute_nest_tier failed:', e?.message); }
                    try { appearanceState.invalidateCache(); } catch(e) {}
                }
            }

            // Run Ink purchase narrative (clerk dialog etc.) if knot exists
            if (item?.purchaseEventPath && storyManager.currentStory) {
                // Close shop overlay, jump to Ink knot → narrative plays → Ink diverts back to shop
                events.emit(Events.OVERLAY_CLOSE, 'overlay-shop');
                setTimeout(() => {
                    try {
                        storyManager.currentStory.ChoosePathString(item.purchaseEventPath);
                        storyManager.refreshView();
                    } catch(e) { console.warn('[Shop] Purchase event failed:', e); }
                }, 300);
            }
        }
    });

    // Shop equip (owned item equip button inside shop overlay)
    events.on('shop:equip', (tag) => {
        // Same gates as wardrobe:equip — equipByTag auto-unequips the occupied
        // slot, so an ungated shop Equip displaces a keyholder-locked cage and
        // bypasses the masc clothing lock from a sibling UI surface.
        if (inventoryManager.isMascClothingFemLocked(tag)) {
            console.log(`[Shop] '${tag}' equip blocked — Femininity past the masc-clothing cap`);
            return;
        }
        try {
            const slot = inventoryManager.getItemByTag(tag)?.slot;
            const occupantTag = slot ? inventoryManager.getEquippedMap().get(slot)?.tag : null;
            // Allow Master-downsize cage-tier swaps (see wardrobe:equip note); still
            // blocks belt/non-cage displacing a locked cage (P1-9).
            if (occupantTag && occupantTag !== tag && inventoryManager.isCageKeyholderLocked(occupantTag)
                && !inventoryManager.isCageDownsizeTier(tag)) {
                console.log(`[Shop] '${tag}' equip blocked — '${occupantTag}' is keyholder-locked`);
                return;
            }
        } catch (e) { console.warn('[Shop] keyholder-lock check failed:', e?.message); }
        inventoryManager.equipByTag(tag);
        inventoryManager.recalculateAllBuffs();
    });

    // Shop money deduction (apartment upgrades, stream equipment — no inventory item)
    events.on('shop:deductMoney', (price) => {
        const money = variableManager.getTotalValue('Money');
        if (money >= price) {
            variableManager.setValue('Money', money - price);
            events.emit(Events.AUDIO_SFX_PLAY, 'coins1');
        }
    });

    // ── Forced event FIFO queue ──────────────────────────────────────
    // Multiple forced events can fire in one morning (SMS, wardrobe, etc).
    // Old approach: one listener per deferred event → race condition, lost events.
    // New approach: single FIFO queue, drain one at a time after each completes.
    // Throttle: max 2 SMS forced events per morning (per Day value).
    const _forcedQueue = [];  // entries: { knot, source }
    let _forcedQueueDraining = false;
    let _smsEventsThisDay = 0;
    let _smsThrottleDay = -1;

    function _enqueueForcedEvent(knot, source) {
        if (!storyManager.currentStory) {
            console.error(`[${source}] Forced event '${knot}' failed, no story loaded`);
            return;
        }
        // Throttle: max 2 phone SMS forced events per day. Pre-fix the queue
        // bypassed this gate — when the throttle pushed to _forcedQueue, the
        // counter wasn't incremented, then _drainForcedQueue called startForcedEvent
        // directly without re-checking. 3rd/4th/5th SMS forced events all
        // eventually fired the same morning. Now: queue stores {knot,source},
        // _drainForcedQueue re-checks the throttle for Phone items and defers
        // them to the next day if cap reached. (Audit 2026-05-23.)
        if (source === 'Phone') {
            const day = storyManager.currentStory.variablesState['Day'] ?? -1;
            if (day !== _smsThrottleDay) {
                _smsEventsThisDay = 0;
                _smsThrottleDay = day;
            }
            if (_smsEventsThisDay >= 2) {
                console.warn(`[SMS Throttle] Deferred '${knot}', max 2 per morning reached`);
                _forcedQueue.push({ knot, source }); // queue for later drain — throttle re-checked on drain
                return;
            }
            _smsEventsThisDay++;
        }
        // Phone-originated events ('Phone' SMS-gotos + 'PhoneApp' sessions) are
        // emitted from an OPEN phone, which cannot coexist with a forced event
        // actually rendering (the phone force-closes on FORCED_EVENT_STARTED).
        // So a still-set _inForcedEvent here is a leaked/stale flag — clear it
        // so the tap fires NOW instead of silently queuing until the next
        // morning's STORY_CHOICES_READY (foundby:Niya Weiss, HypnoMind 0.2.9).
        if ((source === 'PhoneApp' || source === 'Phone')
            && storyManager._inForcedEvent && !_forcedQueueDraining) {
            storyManager.clearStaleForcedEventFlag();
        }
        if (!storyManager._inForcedEvent && !_forcedQueueDraining) {
            storyManager.startForcedEvent(knot);
        } else {
            console.warn(`[${source}] Forced event '${knot}' queued (${_forcedQueue.length + 1} pending)`);
            _forcedQueue.push({ knot, source });
        }
    }

    // F06: drain lock must wrap startForcedEvent in try/finally. Pre-fix
    // released the lock BEFORE calling startForcedEvent, so two parallel
    // setTimeout drains (one per STORY_CHOICES_READY) could both shift items
    // off and call startForcedEvent — the second hit "Blocked nested forced
    // event" guard, dropping its item. Lock must hold across the whole
    // drain attempt; if startForcedEvent throws we still release.
    function _drainForcedQueue() {
        if (_forcedQueueDraining) return;
        if (storyManager._inForcedEvent) return;
        if (_forcedQueue.length === 0) return;
        _forcedQueueDraining = true;
        try {
            const next = _forcedQueue.shift();
            // Support legacy entries that were just `knot` strings (in-flight saves).
            const knot = (typeof next === 'string') ? next : next?.knot;
            const source = (typeof next === 'string') ? null : next?.source;
            // Re-apply SMS throttle on drain — Phone-source items may have
            // been queued because today's cap was already hit. If the cap is
            // still active, push back to queue (next-day reset clears it).
            if (source === 'Phone') {
                const day = storyManager.currentStory?.variablesState?.['Day'] ?? -1;
                if (day !== _smsThrottleDay) {
                    _smsEventsThisDay = 0;
                    _smsThrottleDay = day;
                }
                if (_smsEventsThisDay >= 2) {
                    console.warn(`[SMS Throttle] Re-deferred '${knot}' on drain — cap still reached`);
                    _forcedQueue.push({ knot, source });
                    return;
                }
                _smsEventsThisDay++;
            }
            console.log(`[ForcedQueue] Draining: '${knot}' (${_forcedQueue.length} remaining)`);
            try {
                storyManager.startForcedEvent(knot);
            } catch (e) {
                // startForcedEvent threw — item is already shifted; log loss.
                console.error(`[ForcedQueue] startForcedEvent('${knot}') failed:`, e?.message || e);
            }
        } finally {
            _forcedQueueDraining = false;
        }
    }

    // Drain queue after each forced event returns. F06: dedupe pending
    // setTimeout — multiple STORY_CHOICES_READY emissions in fast succession
    // used to schedule overlapping drains.
    let _pendingDrainTimer = null;
    events.on(Events.STORY_CHOICES_READY, () => {
        if (_forcedQueue.length > 0 && !storyManager._inForcedEvent) {
            if (_pendingDrainTimer) clearTimeout(_pendingDrainTimer);
            _pendingDrainTimer = setTimeout(() => {
                _pendingDrainTimer = null;
                _drainForcedQueue();
            }, 100);
        }
    });

    // Phone forced events. Two flavours share this channel:
    //  - 'Phone'    = SMS [goto:] reply scenes — subject to the 2/day morning
    //                 flood-throttle in _enqueueForcedEvent (default source).
    //  - 'PhoneApp' = VOLUNTARY in-app sessions the player deliberately taps
    //                 (HypnoMind sessions, DatingApps/Grindr meets). These must
    //                 NOT be throttled — they're on-demand, energy/time-gated by
    //                 their own knots, and the SMS cap silently deferred them to
    //                 the next day's first choices-ready ("doesn't play until end
    //                 of day", foundby:Niya Weiss, HypnoMind 0.2.9).
    events.on('phone:forcedEvent', (knot, source) => _enqueueForcedEvent(knot, source || 'Phone'));

    // Wardrobe forced events (plug first-equip scenes)
    events.on('wardrobe:forcedEvent', (knot) => _enqueueForcedEvent(knot, 'Wardrobe'));

    // Location hotspot clicks → select choice
    events.on('location:choiceSelected', (index) => {
        if (storyManager.currentStory) {
            locationsUI.deactivate();
            // 2026-05-21 (foundby:Anne-Sophie): clear the scroll-append buffer
            // when transitioning from map view to a fresh action. Pre-fix the
            // previous action's narration stacked under the new one — player
            // had to scroll all the way down to find the latest result, with
            // the prior shower / journal / etc. card still visible above.
            // KNOT_FAMILIES correctly preserves morning-narration scrollback
            // across the wake-up chain (different transition), but hub → action
            // → map → next action is a different mental beat. Each action
            // should read as its own scene. Clearing here only affects the
            // map-hotspot path — inline-choice scenes still preserve their
            // own scroll context.
            const sc = document.getElementById('story-content');
            if (sc) sc.innerHTML = '';
            storyManager.selectChoice(index);
        }
    });

    // Inventory change → update wardrobe UI + character panel slots
    events.on(Events.ITEM_ADDED, () => {
        wardrobeUI.refresh?.();
    });
    events.on(Events.ITEM_EQUIPPED, (item) => {
        wardrobeUI.refresh?.();
        characterPanelUI.updateSlots();
        if (item?.tag) {
            const itemName = L.getItem(item.tag, 'name', item.displayName || item.name || item.tag);
            const tpl = L.get('ui.toast_equipped');
            const msg = (tpl && tpl !== 'ui.toast_equipped')
                ? tpl.replace('{item}', itemName)
                : `Slipped into the ${itemName}.`;
            events.emit(Events.TOAST_REQUEST, msg);
        }
    });
    events.on(Events.BUFFS_RECALCULATED, () => {
        wardrobeUI.refresh?.();
        characterPanelUI.updateSlots();
    });
    // STORY_CHOICES_READY fires at the end of every refreshView — pick up
    // Ink-side state changes that don't propagate via STAT_CHANGED /
    // BUFFS_RECALCULATED (hair_last_touchup_day after Salon_Hair_Touchup,
    // hrt_last_injection_day after ClinicTreatment, nail_days_remaining
    // after the overnight tick). Foundby:Anne-Sophie 2026-05-20 —
    // "doing Hair Touch-Up doesn't update the day count on the 'you' section"
    // + THS day-count not surfacing at all (HRT-slot rendering added in
    // the same pass).
    events.on(Events.STORY_CHOICES_READY, () => {
        characterPanelUI.updateSlots();
    });

    // Error/notification display (story processing errors, etc.)
    events.on(Events.NOTIFICATION_REQUEST, (text) => {
        statsUI.showToast(text, 'notif-error');
    });

    // Global toast handler (purchases, dares, equips, etc.)
    events.on(Events.TOAST_REQUEST, (message, cssClass) => {
        const toast = document.createElement('div');
        toast.className = 'pno-toast ' + (cssClass || '');
        toast.textContent = message;
        document.body.appendChild(toast);
        setTimeout(() => { toast.classList.add('fade-out'); }, 2500);
        setTimeout(() => { toast.remove(); }, 3000);
    });

    // Audit D04: ACHIEVEMENT_UNLOCKED was emitted by achievements.js:192 but
    // nothing in the codebase listened — 102 achievements popped silently
    // (foundby audit 2026-05-11). Wire a distinct trophy-styled toast +
    // SFX so unlocks register. Stacks naturally — multiple unlocks in the
    // same tick render side-by-side.
    events.on(Events.ACHIEVEMENT_UNLOCKED, (id, title, desc) => {
        const toast = document.createElement('div');
        toast.className = 'pno-achievement-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        const safeTitle = String(title || id || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'})[c]);
        const safeDesc = String(desc || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'})[c]);
        toast.innerHTML = `
            <div class="pno-achievement-toast-icon">\u{1F3C6}</div>
            <div class="pno-achievement-toast-body">
                <div class="pno-achievement-toast-eyebrow">Achievement unlocked</div>
                <div class="pno-achievement-toast-title">${safeTitle}</div>
                ${safeDesc ? `<div class="pno-achievement-toast-desc">${safeDesc}</div>` : ''}
            </div>
        `;
        toast.addEventListener('click', () => {
            layoutManager.openOverlay?.('overlay-achievement');
            toast.classList.add('fade-out');
            setTimeout(() => toast.remove(), 400);
        });
        document.body.appendChild(toast);
        try { events.emit(Events.AUDIO_SFX_PLAY, 'special_pop1'); } catch(e) {}
        setTimeout(() => { toast.classList.add('fade-out'); }, 4500);
        setTimeout(() => { toast.remove(); }, 5000);
    });

    // Location views → activate on choices
    events.on(Events.STORY_CHOICES_READY, (choices) => {
        // 2026-05-21 (foundby:VPS player 0.2.6.6 Day 104): map AND inline
        // choices were both visible on hub re-entry. Pre-fix only `#choices`
        // (legacy fallback) was toggled, but choices render in `#choices-inline`
        // inside #story-content (scroll-append). Pre-2026-05-19 the
        // locations.js storyContent.innerHTML wipe hid the inline container as
        // a side effect; once that wipe was removed (a34727aa) the inline
        // choices stayed visible behind the map. Toggle both containers.
        //
        // 2026-05-21 follow-up: also hide the WHOLE #story-content while the
        // map is up. The narration card + image-wrapper from the prior knot
        // were leaking under the map (player report: "apartment + outside
        // bg image still shows under the map"). Hiding instead of wiping
        // preserves scrollback for the next non-map scene transition.
        const choicesEl = document.getElementById('choices');
        const choicesInline = document.getElementById('choices-inline');
        const storyContentEl = document.getElementById('story-content');
        const _setChoicesHidden = (hide) => {
            if (choicesEl) choicesEl.classList.toggle('hidden', hide);
            if (choicesInline) choicesInline.style.display = hide ? 'none' : '';
        };
        // Map-mode helper: also hide #story-content (narration cards + image
        // wrappers from the prior knot). Only used in the location branch
        // because the Computer overlay needs story-content visible for its
        // browser chrome.
        const _setStoryContentHidden = (hide) => {
            if (storyContentEl) storyContentEl.style.display = hide ? 'none' : '';
        };

        // P0: Computer Desktop/Browser — takes priority over location maps
        // (Computer choices contain keywords like "Streaming" that match apartment hotspots)
        if (computerUI.isActive()) {
            locationsUI.deactivate();
            computerUI.renderChoices(choices, (index) => storyManager.selectChoice(index));
            const computerVisible = computerUI._root && !computerUI._root.classList.contains('hidden');
            _setChoicesHidden(computerVisible);
            // Computer needs #story-content visible (chrome injection target).
            // If we just came from a map turn it may still be display:none.
            _setStoryContentHidden(false);
        } else {
            // Location detection priority:
            // 1. travelLocation (explicit changeLocation call)
            // 2. currentLocation (updated from choice sourcePath in _createChoicesUI)
            // 3. lastBGM (from #bgm: tag — every location sets this, most reliable fallback)
            const locationHint = storyManager.travelLocation || storyManager.currentLocation || storyManager.lastBGM || '';

            const locationMapActive = locationsUI.tryActivate(choices, locationHint);
            // Clear travelLocation after use so it doesn't persist into story navigation
            storyManager.travelLocation = null;

            _setChoicesHidden(locationMapActive);
            _setStoryContentHidden(locationMapActive);

            // Scene-Mode toggle: hubs (location maps) keep full UI,
            // narrative scenes auto-collapse sidebars + cap choices.
            // computer/streaming/shop overlays handle their own UI mode.
            // User can disable Scene Mode globally via Settings → body.scene-mode-disabled.
            const sceneModeAllowed = !document.body.classList.contains('scene-mode-disabled');
            const wasSceneMode = document.body.classList.contains('scene-mode');
            const nowSceneMode = sceneModeAllowed && !locationMapActive;
            document.body.classList.toggle('scene-mode', nowSceneMode);
            // Sticky sidebars (foundby:CutieLvr): remember the player's open/closed
            // choice across scenes so panels don't keep re-collapsing every beat.
            // Desktop + tablet (both use the user-expanded override). Phone uses
            // slide-in drawers instead, so it's excluded.
            if (nowSceneMode && !layoutManager.isPhone) {
                const wantL = localStorage.getItem('PnO_SceneSidebarLeft') === '1';
                const wantR = localStorage.getItem('PnO_SceneSidebarRight') === '1';
                document.getElementById('left-column')?.classList.toggle('user-expanded', wantL);
                document.getElementById('right-column')?.classList.toggle('user-expanded', wantR);
            } else if (wasSceneMode && !nowSceneMode) {
                document.getElementById('left-column')?.classList.remove('user-expanded');
                document.getElementById('right-column')?.classList.remove('user-expanded');
            }
        }

        // P0: Streaming — apply action intensity classes + icons to choice buttons
        if (streamingUI.isActive()) {
            requestAnimationFrame(() => {
                const btns = document.querySelectorAll('#choices .choice-btn');
                btns.forEach(btn => {
                    const textEl = btn.querySelector('.choice-text');
                    const text = textEl?.textContent || '';
                    const actionClass = streamingUI.getActionClass(text);
                    if (actionClass) btn.classList.add(actionClass);
                    // Inject stream action icon before choice text
                    if (textEl && !textEl.querySelector('.stream-action-icon')) {
                        const iconPath = streamingUI.getActionIcon(text);
                        if (iconPath) {
                            const img = document.createElement('img');
                            img.src = iconPath;
                            img.className = 'stream-action-icon';
                            img.width = 22;
                            img.height = 22;
                            img.alt = '';
                            img.setAttribute('aria-hidden', 'true');
                            // Insert after the number span, before text
                            const numSpan = textEl.querySelector('.choice-number');
                            if (numSpan && numSpan.nextSibling) {
                                textEl.insertBefore(img, numSpan.nextSibling);
                            } else {
                                textEl.prepend(img);
                            }
                        }
                    }
                });
            });
        }
    });

    // P0: Computer shutdown/close — find and auto-select the exit choice
    events.on('computer:shutdown', () => _exitComputer(['shut down', 'close', 'back', 'leave', 'power off', 'stand up']));
    events.on('computer:browserClose', () => _exitComputer(['close', 'back', 'leave', 'shut down', 'stand up']));

    async function _exitComputer(keywords) {
        console.log('[PnO] _exitComputer: deactivating, keywords:', keywords);
        computerUI._deactivate();
        // Wait for any pending processing to finish
        for (let i = 0; i < 20; i++) {
            if (!storyManager.isProcessing) break;
            await new Promise(r => setTimeout(r, 100));
        }
        const story = storyManager.currentStory;
        if (!story) { console.warn('[PnO] _exitComputer: no story!'); return; }
        const choices = story.currentChoices;
        console.log('[PnO] _exitComputer: ' + choices.length + ' ink choices:', choices.map(c => c.text.trim()).join(' | '));
        let found = false;
        for (const keyword of keywords) {
            for (const c of choices) {
                if (c.text.toLowerCase().includes(keyword)) {
                    console.log('[PnO] _exitComputer: matched "' + keyword + '" → choice ' + c.index + ': "' + c.text.trim() + '"');
                    storyManager.selectChoice(c.index);
                    found = true;
                    break;
                }
            }
            if (found) break;
        }
        if (!found) {
            console.warn('[PnO] _exitComputer: NO MATCH! Falling back to last choice');
            if (choices.length > 0) storyManager.selectChoice(choices[choices.length - 1].index);
            else console.error('[PnO] _exitComputer: NO CHOICES AT ALL');
        }
    }

    // Debug teleport (cheat panel in phone)
    events.on('LOCATION_CHANGE', (knot) => {
        if (storyManager.currentStory) {
            storyManager.forceJump(knot);
        }
    });

    // P2: Phone app open — dispatch to app-specific views
    events.on('phone:appOpen', (appId) => {
        phoneUI.openApp(appId);
    });

    // P4: Morning Routine Panel — overlay with automation toggles
    events.on(Events.TAG_UI, async (mode) => {
        if (mode === 'planner') {
            plannerUI.show();
        } else if (mode === 'endscreen') {
            // Read which ending fired this run (set by the Mirror knot
            // immediately before #ui:endscreen). Default to 'kept' if missing.
            let key = '';
            try { key = variableManager._story?.variablesState?.['ending_reached'] || ''; } catch(e) {}
            if (!ENDSCREEN_KEYS.has(key)) key = 'kept';
            const { endscreen } = await import('./ui/endscreen.js');
            endscreen.show(key);
        } else if (mode === 'mainmenu') {
            try { saveSystem.flushPendingSync?.(); } catch(e) {}
            // RELOAD, not SCREEN_CHANGE. Reached from Endgame_ReturnToMenu
            // (post-ending), so the main-menu DOM was already unmounted on
            // game-view entry — an in-memory screen change shows an empty black
            // menu. Reload rebuilds it (matches pause-menu Quit). foundby:Monki.
            location.reload();
        }
    });

    // Nude view toggle (Legacy-Event, z.B. aus Tests) -> Tab-System
    events.on(Events.VARIABLE_CHANGED, (name, value) => {
        if (name === 'nudeView') {
            characterPanelUI.setPanelMode(value ? 'nude' : 'clothed');
        }
    });
}

// ============================================
// Main Menu
// ============================================
/**
 * Game-entry gate for the parallelized tar boot: scene images resolve only
 * from the archive, so entering a run before it's indexed would render the
 * first beats image-less (placeholders + heal cover stragglers, but don't
 * make it the default experience). Joins the in-flight download; instant
 * once loaded or in loose mode. On failure the session enters degraded
 * mode — the failure banner + background retry own recovery.
 */
async function _ensureImagesReady(btn) {
    try {
        const st = getTarStatus();
        if (st.mode !== 'tar' || st.loaded) return;
    } catch (e) { return; }
    const orig = btn ? btn.textContent : null;
    if (btn) {
        btn.disabled = true;
        btn.textContent = L.get('ui.images_loading', 'Loading images...');
    }
    try { await loadImageTar(); } catch (e) { /* degraded mode */ }
    // Late/gated tar load: re-run the full interceptor pass — the incremental
    // observer (leak fix 2026-07-02) won't revisit elements that were checked
    // while the archive was still empty.
    try { _interceptAllImages(); } catch (e) { /* non-fatal */ }
    if (btn) {
        btn.disabled = false;
        if (orig !== null) btn.textContent = orig;
    }
}

/**
 * Non-blocking "images failed" banner with a Retry button. Driven by the
 * pno-tar-state events image-loader emits on every state transition; hides
 * itself the moment a heal lands.
 */
function _wireTarStateBanner() {
    let banner = null;
    window.addEventListener('pno-tar-state', (e) => {
        const st = e.detail || {};
        if (st.complete) {
            if (banner) { banner.remove(); banner = null; }
            return;
        }
        if (banner || st.mode !== 'tar') return;
        banner = document.createElement('div');
        banner.id = 'tar-retry-banner';
        banner.style.cssText = 'position:fixed;bottom:12px;left:50%;transform:translateX(-50%);z-index:9999;background:#2a1f3d;color:#e8e0f5;padding:8px 14px;border-radius:8px;border:1px solid #6e3c7a;font-size:13px;display:flex;gap:10px;align-items:center;box-shadow:0 4px 16px rgba(0,0,0,.5)';
        const txt = document.createElement('span');
        txt.textContent = L.get('ui.images_load_failed', 'Some images failed to load');
        const btn = document.createElement('button');
        btn.textContent = L.get('ui.images_retry', 'Retry');
        btn.style.cssText = 'background:#6e3c7a;color:#fff;border:none;border-radius:6px;padding:4px 12px;cursor:pointer;font-size:13px';
        btn.addEventListener('click', () => {
            btn.disabled = true;
            retryTarNow().finally(() => { btn.disabled = false; });
        });
        const x = document.createElement('button');
        x.textContent = '×';
        x.setAttribute('aria-label', 'Dismiss');
        x.style.cssText = 'background:none;color:#887ca0;border:none;cursor:pointer;font-size:16px;padding:0 2px';
        x.addEventListener('click', () => { if (banner) { banner.remove(); banner = null; } });
        banner.append(txt, btn, x);
        document.body.appendChild(banner);
    });
}

function bindMainMenu() {
    const btnNew = document.getElementById('btn-new-game');
    const btnContinue = document.getElementById('btn-continue');
    const btnLoad = document.getElementById('btn-load-game');
    const btnSettings = document.getElementById('btn-settings-toggle');
    const menuSettings = document.getElementById('menu-settings');

    if (btnNew) {
        btnNew.addEventListener('click', async () => {
            // Parallelized-boot gate: editor vignettes + first scene come
            // from the tar; wait for it here (instant when already loaded).
            await _ensureImagesReady(btnNew);
            // First-run UI profile (owner call 2026-07-16): the picker dialog
            // is retired — Stats Visible is the standard, switchable anytime
            // in Settings > Display. Apply the stats profile ONCE on virgin
            // browsers only; anyone who chose via the old dialog or hand-set
            // any of the three keys keeps their values untouched.
            if (localStorage.getItem('PnO_OnboardingProfileChosen') !== 'true') {
                // Virgin = the player never toggled Scene Mode or Text Speed.
                // (StatPreview is excluded: settings.js auto-writes it at boot,
                // so it's never null even on a fresh browser — SceneMode and
                // TextSpeed only persist on an active user change.)
                const virgin = localStorage.getItem('PnO_SceneMode') === null
                    && localStorage.getItem('PnO_TextSpeed') === null;
                if (virgin) _applyOnboardingProfile('stats');
                try {
                    localStorage.setItem('PnO_OnboardingProfileChosen', 'true');
                    localStorage.setItem('PnO_OnboardingProfile', virgin ? 'stats' : 'skip');
                } catch (e) { /* private browsing — defaults still applied via classes */ }
            }
            _enterCharacterEditor();
        });
    }

    // Audit D32: Editor exit-affordance. Top-left back button on the editor
    // routes to the main menu without requiring step-back-Quit-confirm.
    const btnEditorExit = document.getElementById('btn-editor-exit');
    if (btnEditorExit) {
        btnEditorExit.addEventListener('click', () => {
            events.emit(Events.SCREEN_CHANGE, 'screen-main-menu');
        });
    }

    function _enterCharacterEditor() {
        characterEditorUI.resetState();
        document.querySelectorAll('#screen-character-editor .card.selected').forEach(c => c.classList.remove('selected'));
        // Audit D25 fix: was '.editor-phase' + '.phase-dot' — neither selector
        // exists in the markup, so phase reset was a silent no-op. The actual
        // classes are '.phase' (sections) and '.progress-dots .dot' (header).
        // Result of the silent miss: a returning player who quit during phase 3
        // landed back on phase 3 with stale .active classes, no progress-dot
        // reset, and stale selections still rendering as 'selected'.
        const phases = document.querySelectorAll('#screen-character-editor .phase');
        phases.forEach((p, i) => {
            p.classList.toggle('active', i === 0);
            p.classList.remove('phase-exit-left', 'phase-exit-right', 'phase-enter-left', 'phase-enter-right');
        });
        document.querySelectorAll('#screen-character-editor .progress-dots .dot').forEach((d, i) => {
            d.classList.remove('active', 'done');
            if (i === 0) d.classList.add('active');
        });
        // Reset trait counter / Continue-gate so re-entering the editor doesn't
        // show stale 2/2 + enabled Continue from a previous run.
        const counterNum = document.getElementById('trait-counter-num');
        if (counterNum) counterNum.textContent = '0';
        const counter = document.getElementById('trait-counter');
        if (counter) counter.classList.remove('trait-counter-ready');
        const traitsPhase = document.getElementById('phase-traits');
        if (traitsPhase) {
            const cb = traitsPhase.querySelector('.btn-next');
            if (cb) { cb.disabled = true; cb.setAttribute('aria-disabled', 'true'); }
        }
        // Clear name inputs so a fresh character doesn't inherit the previous
        // run's names (incl. a Master name that may not fit the new intensity).
        ['input-name', 'input-fem-name', 'input-master', 'input-gf'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.value = '';
        });
        // Clear the skip-setup checkbox too — since 0.3.1 the archetype path
        // never renders the phase that hosts it, so a stale check from an
        // abandoned wizard visit would silently ship a Day-8 start.
        const skipCb = document.getElementById('skip-setup-checkbox');
        if (skipCb) skipCb.checked = false;
        // Re-apply the visual highlight for Phase-2's pre-selected defaults
        // (Roommate intensity + Girlfriend) + repaint intensity-detail/difficulty,
        // which the reset above strips while keeping the underlying values.
        try { characterEditorUI.applyDefaultSelections(); } catch (e) { /* editor not built yet */ }
        const stepLabelEl = document.getElementById('editor-step-label');
        if (stepLabelEl) stepLabelEl.textContent = 'Step 1 of 6';
        // 0.3.1 archetype quick-start: New Game lands on the persona picker;
        // the wizard phases above stay reset underneath ("Build Your Own").
        // showEntry AFTER the screen switch (same sync task, no flash) so its
        // heading-focus lands on a rendered element instead of a display:none
        // subtree (review finding 2026-07-15).
        events.emit(Events.SCREEN_CHANGE, 'screen-character-editor');
        try { characterEditorUI.showEntry(); } catch (e) { console.warn('[PnO] showEntry failed:', e?.message); }
    }

    // Onboarding picker DIALOG retired 2026-07-16 (owner call): Stats Visible
    // is the standard profile, applied once on virgin browsers at New Game.
    // The #overlay-onboarding-profile markup stays hidden in index.html (keeps
    // its L10n keys referenced); Settings > Display remains the switch surface.
    function _applyOnboardingProfile(profile) {
        let _quotaWarned = false;
        const set = (k, v) => {
            try { localStorage.setItem(k, v); }
            catch (e) {
                if (!_quotaWarned) {
                    console.warn(`[Onboarding] localStorage.setItem('${k}') failed (private browsing?):`, e?.message);
                    _quotaWarned = true;
                }
            }
        };
        if (profile === 'story') {
            set('PnO_SceneMode', '1');
            set('PnO_StatPreview', '0');
            set('PnO_TextSpeed', '0.4');
        } else if (profile === 'stats') {
            set('PnO_SceneMode', '0');
            set('PnO_StatPreview', '1');
            set('PnO_TextSpeed', '0');
        }
        // 'skip' falls through — leave existing defaults / null untouched.
        // settings.js applies these on next init / panel render.
        document.body.classList.toggle('hide-stat-previews',
            localStorage.getItem('PnO_StatPreview') !== '1');
        document.body.classList.toggle('scene-mode-disabled',
            (localStorage.getItem('PnO_SceneMode') ?? '1') !== '1');
    }

    // Quick Start button removed 0.3.1 — the archetype screen inside New Game
    // is the quick start now. characterEditorUI.quickStart() survives as the
    // programmatic default-start (tests via window._charEditor).

    // Lovense main-menu CTA — Patreon upsell + quick entry for existing Patrons.
    // Non-Patreon: opens the Patreon subscription page in a new tab (same destination
    // as the primary Patreon CTA, but with a Lovense-specific entry point so we can
    // later track conversion if needed).
    // Patreon: opens the pause/settings overlay and mounts the Toys section, so
    // players who already subscribed can get straight to pairing.
    const btnLovenseCta = document.getElementById('btn-cta-lovense');
    if (btnLovenseCta) {
        btnLovenseCta.addEventListener('click', () => {
            if (isPat()) {
                lovenseSettingsUI.mount();
                layoutManager.openOverlay('overlay-pause');
                // Scroll the pause content so the Toys section is visible
                setTimeout(() => {
                    document.querySelector('#overlay-pause .pause-lovense')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 120);
            } else {
                window.open('https://www.patreon.com/PinkedAndOwned', '_blank', 'noopener');
            }
        });
    }

    if (btnContinue) {
        let _continueInProgress = false;
        btnContinue.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (_continueInProgress) return; // Guard against double-click
            _continueInProgress = true;
            await _ensureImagesReady(btnContinue);
            try {
                // Load auto-save first
                const saveData = await saveSystem.loadAutoSave();
                if (!saveData || !saveData.inkState) {
                    console.warn('[PnO] No auto-save found');
                    // Disable button visually
                    btnContinue.style.opacity = '0.3';
                    btnContinue.style.pointerEvents = 'none';
                    _continueInProgress = false;
                    return;
                }

                // Show loading state on button
                const origText = btnContinue.textContent;
                btnContinue.textContent = 'Loading...';
                btnContinue.style.opacity = '0.6';

                await bootAndApplySave(saveData);
                console.log('[PnO] Continue from auto-save successful');
            } catch (err) {
                console.error('[PnO] Continue failed:', err);
            } finally {
                _continueInProgress = false;
            }
        });
    }

    if (btnLoad) {
        btnLoad.addEventListener('click', () => {
            // foundby:Guy98 0.2.6.7 — without refreshSlots() the overlay
            // opens with every slot frozen at the static "Empty" HTML
            // default, so players on main menu thought they had no saves
            // and were forced to New Game → in-game pause to find them.
            // In-game settings button (main.js:2360) already did this;
            // the main-menu entry point was missing the call.
            try { saveLoadUI.refreshSlots(); } catch(e) { console.warn('[MainMenu Load] refreshSlots failed:', e); }
            try { saveLoadUI.refreshCloudList?.(); } catch(e) { console.warn('[MainMenu Load] refreshCloudList failed:', e); }
            try { settingsUI.refreshCloudSaveUI?.(); } catch(e) { console.warn('[MainMenu Load] refreshCloudSaveUI failed:', e); }
            layoutManager.openOverlay('overlay-pause');
        });
    }

    if (btnSettings && menuSettings) {
        btnSettings.addEventListener('click', () => {
            menuSettings.classList.toggle('hidden');
            btnSettings.textContent = menuSettings.classList.contains('hidden') ? 'Settings' : 'Close Settings';
        });
    }

    const btnCredits = document.getElementById('btn-credits-toggle');
    if (btnCredits) {
        btnCredits.addEventListener('click', () => creditsUI.open());
    }

    // Settings sliders
    settingsUI.bindSliders();
}

function updateContinueButton() {
    const btnContinue = document.getElementById('btn-continue');
    if (!btnContinue) return;

    // Audit D23: visible-but-disabled state. Previously full pointer-events:none
    // with 0.3 opacity gave no signal WHY it's dim. Use aria-disabled + title +
    // a class so CSS can render proper disabled affordance, not just fading.
    const setDisabled = (disabled, reason) => {
        btnContinue.classList.toggle('menu-continue-disabled', disabled);
        if (disabled) {
            btnContinue.setAttribute('aria-disabled', 'true');
            btnContinue.title = reason || 'No save data yet';
            btnContinue.style.opacity = '0.45';
            btnContinue.style.pointerEvents = 'none';
        } else {
            btnContinue.removeAttribute('aria-disabled');
            btnContinue.title = 'Continue your last run';
            btnContinue.style.opacity = '1';
            btnContinue.style.pointerEvents = 'auto';
        }
    };

    try {
        // Check auto-save OR any manual save slot (from cache)
        const hasSave = saveSystem.autoSaveExists()
            || saveSystem.slotExists(0)
            || saveSystem.slotExists(1)
            || saveSystem.slotExists(2);
        setDisabled(!hasSave, 'No save data yet, start a new game first');
    } catch (e) {
        setDisabled(true, 'Save data unavailable');
    }
}

// Tutorial Tips — extracted to ui/tutorial-tips.js



// P3: loadGameIncremental — extracted to systems/game-loader.js (imported at top)




// Character Editor — extracted to ui/character-editor.js (imported at top)


// ============================================
// Game View Controls
// ============================================
function bindGameControls() {
    // Menu button — handled in pause menu section below (bindGameControls runs before that)

    // Wait button — advance one time slot (mirrors ArrowRight + .time-seg click).
    const btnWait = document.getElementById('btn-wait');
    if (btnWait) {
        btnWait.addEventListener('click', () => {
            const active = document.querySelector('.time-seg.active');
            if (!active) return;
            const idx = parseInt(active.dataset.slot);
            const next = document.querySelector(`.time-seg[data-slot="${idx + 1}"]`);
            if (next) next.click();
        });
    }

    // Time segments — click handler is in bindGameControls() (lines 2200+), not here

    // Action buttons — feature overlays use their own open() which builds UI
    document.querySelectorAll('.action-btn[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
            const action = btn.dataset.action;
            // Dismiss any open Phone overlay before opening a non-phone feature
            // overlay. Without this, clicking Gallery / Journal / Log /
            // Achievements / Wardrobe while the Phone is open layered the new
            // overlay behind the Phone (z-index stack: Phone won). Player saw
            // an empty-looking Phone screen with the requested feature hidden
            // underneath. (0.2.7 playtest finding.)
            if (action !== 'phone') {
                const phoneEl = document.getElementById('overlay-phone');
                if (phoneEl && !phoneEl.classList.contains('hidden')) {
                    layoutManager.closeOverlay('overlay-phone');
                }
            }
            switch (action) {
                case 'wardrobe': {
                    // Feed current inventory data, then open
                    const ownedItems = inventoryManager.getOwnedItemObjects();
                    const equippedMap = inventoryManager.getEquippedMap();
                    wardrobeUI.updateInventory(ownedItems);
                    wardrobeUI.updateEquipped(equippedMap);
                    wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
                    wardrobeUI.open();
                    layoutManager.openOverlay('overlay-wardrobe', false);
                    break;
                }
                case 'phone':
                    phoneUI.open();
                    break;
                case 'log': historyUI.render(); layoutManager.openOverlay('overlay-history'); break;
                case 'journal': layoutManager.openOverlay('overlay-journal'); journalUI.renderActive(); break;
                case 'gallery':
                    galleryUI.open?.();
                    if (!galleryUI.open) layoutManager.openOverlay('overlay-gallery');
                    break;
                case 'achievements': layoutManager.openOverlay('overlay-achievement'); break;
                case 'settings': document.getElementById('btn-settings')?.click(); break;
            }
        });
    });

    // ------------------------------------------------------------------
    // Keyboard Shortcut Handlers (emitted from layout.js)
    // These need module access (phoneUI, wardrobeUI, galleryUI, locationsUI)
    // ------------------------------------------------------------------

    events.on('shortcut:phone', () => {
        const el = document.getElementById('overlay-phone');
        if (el && !el.classList.contains('hidden')) {
            layoutManager.closeOverlay('overlay-phone');
        } else {
            phoneUI.open();
        }
    });

    events.on('shortcut:gallery', () => {
        const el = document.getElementById('overlay-gallery');
        if (el && !el.classList.contains('hidden')) {
            layoutManager.closeOverlay('overlay-gallery');
        } else {
            // Dismiss Phone first (see action-btn handler comment).
            const phoneEl = document.getElementById('overlay-phone');
            if (phoneEl && !phoneEl.classList.contains('hidden')) {
                layoutManager.closeOverlay('overlay-phone');
            }
            galleryUI.open?.();
            if (!galleryUI.open) layoutManager.openOverlay('overlay-gallery');
        }
    });

    events.on('shortcut:wardrobe', () => {
        const el = document.getElementById('overlay-wardrobe');
        if (el && !el.classList.contains('hidden')) {
            layoutManager.closeOverlay('overlay-wardrobe');
        } else {
            const ownedItems = inventoryManager.getOwnedItemObjects();
            const equippedMap = inventoryManager.getEquippedMap();
            wardrobeUI.updateInventory(ownedItems);
            wardrobeUI.updateEquipped(equippedMap);
            wardrobeUI.updateSetBonuses(inventoryManager.getActiveSets());
            wardrobeUI.open();
            layoutManager.openOverlay('overlay-wardrobe', false);
        }
    });

    events.on('shortcut:map', () => {
        // Locations map — if a location map overlay exists, toggle it;
        // otherwise trigger the existing location map display
        const mapOverlay = document.getElementById('overlay-map');
        if (mapOverlay) {
            if (!mapOverlay.classList.contains('hidden')) {
                layoutManager.closeOverlay('overlay-map');
            } else {
                layoutManager.openOverlay('overlay-map');
            }
        } else {
            // Fallback: click the map action button if it exists
            const mapBtn = document.querySelector('.action-btn[data-action="map"]');
            if (mapBtn) mapBtn.click();
        }
    });

    // Toggle secondary actions (Journal ▼ toggle, matches Unity)
    const btnMore = document.querySelector('[data-action="toggle-secondary"]');
    const secondary = document.querySelector('.actions-secondary');
    if (btnMore && secondary) {
        btnMore.addEventListener('click', () => {
            secondary.classList.toggle('hidden');
            const jText = L.get('ui.journal');
            btnMore.innerHTML = secondary.classList.contains('hidden') ? jText + ' \u25BC' : jText + ' \u25B2';
        });
    }
    // Bug report submit — sends to VPS only
    const BUGREPORT_VPS_URL = 'https://www.pinkedandowned.com/api/analytics/bugreport';
    let bugCooldownUntil = 0;

    // Anonymous checkbox — hide/show contact fields
    const anonCheckbox = document.getElementById('bugreport-anon');
    const contactFields = document.getElementById('bugreport-contact-fields');
    if (anonCheckbox && contactFields) {
        anonCheckbox.addEventListener('change', () => {
            contactFields.style.display = anonCheckbox.checked ? 'none' : '';
            if (anonCheckbox.checked) {
                const emailEl = document.getElementById('bugreport-email');
                const nickEl = document.getElementById('bugreport-nickname');
                if (emailEl) emailEl.value = '';
                if (nickEl) nickEl.value = '';
            }
        });
    }

    // Attach-save status line — probes buildSaveData so the player can see
    // whether the save will actually ride along. Collection failures used to
    // fail silently (v0.2.4.1 Amanda S bugreport), so people thought they
    // attached the save when the payload never contained it.
    // Also force-defaults the checkbox to checked on every overlay open so
    // Firefox's form-state autofill can't leave it unchecked from a previous
    // session.
    function _updateBugReportAttachInfo() {
        const cb = document.getElementById('bugreport-attach-save');
        const info = document.getElementById('bugreport-attach-info');
        const consentRow = document.getElementById('bugreport-save-consent-row');
        const consentCb = document.getElementById('bugreport-save-consent');
        if (!cb || !info) return;
        // Art. 9(2)(a): the explicit-consent row is only relevant when the
        // player wants to attach a save. Show/hide it with the attach toggle.
        if (consentRow) consentRow.style.display = cb.checked ? '' : 'none';
        if (!cb.checked) {
            info.textContent = '(not attached)';
            info.style.color = 'var(--text-dim)';
            return;
        }
        if (!saveSystem._collectors) {
            info.textContent = L.get('main.debug.no_save_yet', '⚠ no save yet (start a game first)');
            info.style.color = 'var(--accent)';
            return;
        }
        // Attach requested but consent not yet given → make it explicit.
        if (consentCb && !consentCb.checked) {
            info.textContent = L.get('bugreport.attach_need_consent', '(consent required)');
            info.style.color = 'var(--accent)';
            return;
        }
        try {
            const data = saveSystem.buildSaveData(saveSystem._collectors);
            const bytes = JSON.stringify(data).length;
            const kb = (bytes / 1024).toFixed(1);
            info.textContent = `✓ ${kb} KB`;
            info.style.color = 'var(--energy)';
        } catch (e) {
            info.textContent = `⚠ collect failed: ${e.message || e}`;
            info.style.color = 'var(--accent)';
        }
    }
    const attachCb = document.getElementById('bugreport-attach-save');
    if (attachCb) {
        attachCb.addEventListener('change', _updateBugReportAttachInfo);
    }
    const attachConsentCb = document.getElementById('bugreport-save-consent');
    if (attachConsentCb) {
        attachConsentCb.addEventListener('change', _updateBugReportAttachInfo);
    }
    // Refresh the status + reset the checkbox every time the overlay opens.
    // Exposed globally so the two openOverlay('overlay-bugreport') call sites
    // in this file can prime the status line immediately after open (the
    // layout manager doesn't emit an 'opened' event we could listen to).
    window._openBugReportOverlay = () => {
        const cb = document.getElementById('bugreport-attach-save');
        if (cb) cb.checked = true;
        // Consent must be re-given each session — never default-checked
        // (Art. 9(2)(a) explicitness; consent can't be pre-ticked).
        const consentCb = document.getElementById('bugreport-save-consent');
        if (consentCb) consentCb.checked = false;
        _updateBugReportAttachInfo();
    };

    const btnBugSubmit = document.getElementById('btn-bugreport-submit');
    if (btnBugSubmit) {
        btnBugSubmit.addEventListener('click', async () => {
            const isAnon = anonCheckbox?.checked || false;
            const subject = document.getElementById('bugreport-subject')?.value || '';
            const desc = document.getElementById('bugreport-description')?.value || '';
            const email = isAnon ? '' : (document.getElementById('bugreport-email')?.value || '');
            const nickname = isAnon ? '' : (document.getElementById('bugreport-nickname')?.value || '');
            const status = document.getElementById('bugreport-status');

            if (!subject && !desc) {
                if (status) status.textContent = L.get('ui.fill_one_field');
                return;
            }

            const now = Date.now();
            if (now < bugCooldownUntil) {
                const secs = Math.ceil((bugCooldownUntil - now) / 1000);
                if (status) status.textContent = L.get('ui.wait_cooldown').replace('{0}', secs);
                return;
            }
            bugCooldownUntil = now + 60000;

            const gameState = {
                day: variableManager.getValue('Day'),
                timeSlot: variableManager.getValue('TimeSlot'),
                fem: variableManager.getValue('Femininity'),
                sub: variableManager.getValue('Submissiveness'),
                arousal: variableManager.getValue('Arousal'),
                energy: variableManager.getValue('Energy'),
                money: variableManager.getValue('Money'),
                location: storyManager.currentLocation,
                build: isPat() ? 'Patreon' : 'Free',
                // Asset ground truth for "images not loading" reports — without
                // this the only console line is often the non-fatal Cache-write
                // warn and the report is undiagnosable (0.3 report #406).
                tar: (() => { try { return window.__tarStatus ? window.__tarStatus() : null; } catch (e) { return null; } })(),
            };
            const contactInfo = [email ? `Email: ${email}` : '', nickname ? `Nickname: ${nickname}` : ''].filter(Boolean).join(' | ') || 'N/A';
            const version = VERSION_INFO.fullVersion;
            const recentLogs = (window._consoleLog?.buffer || [])
                .filter(e => e.l === 'E' || e.l === 'W')
                .slice(-15)
                .map(e => `[${e.l}] ${e.m}`)
                .join('\n');

            // Collect save data if checkbox is checked. Capture the collection
            // outcome so we can surface it in the final submit-status message —
            // silent failures (v0.2.4.1 / Amanda S) made players think they'd
            // attached a save when the payload actually didn't contain one.
            // Art. 9(2)(a): the save is only attached when BOTH the attach
            // checkbox AND the explicit-consent checkbox are ticked. Either
            // unchecked → no save_data in the payload.
            const attachWanted = document.getElementById('bugreport-attach-save')?.checked;
            const attachConsented = document.getElementById('bugreport-save-consent')?.checked;
            const attachSave = !!(attachWanted && attachConsented);
            let saveData = null;
            let saveAttachNote = '';
            if (attachWanted && !attachConsented) {
                saveAttachNote = ' (save not attached: consent not given)';
            }
            if (attachSave) {
                if (!saveSystem._collectors) {
                    saveAttachNote = ' (save not attached: no game loaded)';
                } else {
                    try {
                        saveData = saveSystem.buildSaveData(saveSystem._collectors);
                        const kb = (JSON.stringify(saveData).length / 1024).toFixed(1);
                        saveAttachNote = ` (save attached: ${kb} KB)`;
                    } catch(e) {
                        console.warn('[BugReport] Save collection failed:', e);
                        saveAttachNote = ` (save not attached: ${e.message || e})`;
                    }
                }
            }

            if (status) { status.textContent = L.get('ui.sending'); status.style.color = ''; }

            // Attach diagnostic buffers so opted-out-of-analytics players still
            // ship them when they actively report a bug. Same shapes as the
            // session-payload fields for VPS-side parity.
            let bracketBugs = null;
            try {
                const arr = window.__bracketBugs || [];
                if (arr.length) {
                    const byKey = {};
                    for (const b of arr) byKey[b.key] = (byKey[b.key] || 0) + 1;
                    bracketBugs = { count: arr.length, by_key: byKey, sample: arr.slice(-5).map(b => ({ k: b.key, knot: b.knot })) };
                }
            } catch(e) {}
            let stateViolations = null;
            try {
                const arr = window.__stateViolations || [];
                if (arr.length) {
                    stateViolations = { count: arr.length, keys: arr.map(v => v.key), sample: arr.slice(-5) };
                }
            } catch(e) {}

            try {
                const payload = { subject, description: desc, game_state: gameState, version, platform: navigator.userAgent.substring(0, 80), console_log: recentLogs || '', contact: contactInfo };
                if (saveData) payload.save_data = saveData;
                if (bracketBugs) payload.bracket_bugs = bracketBugs;
                if (stateViolations) payload.state_violations = stateViolations;
                const res = await fetch(BUGREPORT_VPS_URL, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(payload)
                });
                if (res.ok) {
                    if (status) { status.textContent = L.get('ui.report_sent') + saveAttachNote; status.style.color = 'var(--energy)'; }
                    events.emit(Events.TOAST_REQUEST, L.get('ui.report_sent'), 'toast-info');
                    setTimeout(() => layoutManager.closeOverlay('overlay-bugreport'), 2500);
                } else {
                    if (status) { status.textContent = L.get('ui.send_failed').replace('{0}', res.status); status.style.color = 'var(--accent)'; }
                }
            } catch(e) {
                if (status) { status.textContent = L.get('ui.network_error'); status.style.color = 'var(--accent)'; }
            }

            // Always store locally as backup
            try {
                const reports = JSON.parse(localStorage.getItem('PnO_BugReports') || '[]');
                reports.push({ subject, description: desc, email, nickname, gameState, consoleLog: recentLogs || '', time: new Date().toISOString() });
                localStorage.setItem('PnO_BugReports', JSON.stringify(reports));
            } catch(e) {}

            if (document.getElementById('bugreport-subject')) document.getElementById('bugreport-subject').value = '';
            if (document.getElementById('bugreport-description')) document.getElementById('bugreport-description').value = '';
        });
    }

    // Feedback submit — sends to VPS only
    const FEEDBACK_VPS_URL = 'https://www.pinkedandowned.com/api/analytics/feedback';
    let feedbackCooldownUntil = 0;
    const btnFeedbackSubmit = document.getElementById('btn-feedback-submit');
    if (btnFeedbackSubmit) {
        btnFeedbackSubmit.addEventListener('click', async () => {
            const category = document.getElementById('feedback-category')?.value || 'general';
            const subject = document.getElementById('feedback-subject')?.value || '';
            const desc = document.getElementById('feedback-description')?.value || '';
            const nickname = document.getElementById('feedback-nickname')?.value || '';
            const status = document.getElementById('feedback-status');

            if (!subject && !desc) {
                if (status) status.textContent = L.get('ui.fill_one_field');
                return;
            }

            const now = Date.now();
            if (now < feedbackCooldownUntil) {
                const secs = Math.ceil((feedbackCooldownUntil - now) / 1000);
                if (status) status.textContent = L.get('ui.wait_cooldown').replace('{0}', secs);
                return;
            }
            feedbackCooldownUntil = now + 60000;

            const version = VERSION_INFO.fullVersion;
            const build = isPat() ? 'Patreon' : 'Free';

            // Best-effort snapshot of player state. Content-feedback often references
            // a specific scene/image without naming it ("the images look male") — the
            // state lets us reconstruct which visuals the player was actually seeing
            // (Day, Fem/Sub, location, key appearance flags). Skipped silently if no
            // active story (main menu feedback).
            let gameState = null;
            try {
                if (storyManager?.currentStory) {
                    // has_visible_breasts / nail_tier / master_intensity are NOT in
                    // OBSERVED_VARIABLES, so getFlag()/getValue() always return
                    // false/0 (they read the JS mirror, which never holds these).
                    // Read them straight from Ink per save-state-drift.md §7, else
                    // every bug-report snapshot reported them as false/0 regardless
                    // of real state — exactly the field a "the images look male /
                    // wrong body" report needs accurate.
                    const _vs = storyManager.currentStory.variablesState;
                    const _inkBool = (n) => { try { const v = _vs[n]; return v === true || v === 1; } catch(e){ return false; } };
                    const _inkNum = (n) => { try { return Number(_vs[n]) || 0; } catch(e){ return 0; } };
                    gameState = {
                        day: variableManager.getValue('Day'),
                        timeSlot: variableManager.getValue('TimeSlot'),
                        fem: variableManager.getValue('Femininity'),
                        sub: variableManager.getValue('Submissiveness'),
                        arousal: variableManager.getValue('Arousal'),
                        energy: variableManager.getValue('Energy'),
                        money: variableManager.getValue('Money'),
                        location: storyManager.currentLocation,
                        build,
                        wearing_chastity: !!variableManager.getFlag('wearing_chastity'),
                        has_long_hair: !!variableManager.getFlag('has_long_hair'),
                        has_visible_breasts: _inkBool('has_visible_breasts'),
                        EquippedFeminine: !!variableManager.getFlag('EquippedFeminine'),
                        EquippedPanties: !!variableManager.getFlag('EquippedPanties'),
                        wearing_wig: !!variableManager.getFlag('wearing_wig'),
                        has_makeup_on: !!variableManager.getFlag('has_makeup_on'),
                        nail_tier: _inkNum('nail_tier'),
                        master_intensity: _inkNum('master_intensity'),
                    };
                }
            } catch(e) { /* best-effort */ }

            if (status) { status.textContent = L.get('ui.sending'); status.style.color = ''; }

            try {
                const payload = { category, message: `${subject}\n${desc}`, version, build, platform: navigator.userAgent.substring(0, 80), nickname: nickname || '' };
                if (gameState) payload.game_state = gameState;
                const res = await fetch(FEEDBACK_VPS_URL, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(payload)
                });
                if (res.ok) {
                    if (status) { status.textContent = L.get('ui.feedback_sent'); status.style.color = 'var(--energy)'; }
                    events.emit(Events.TOAST_REQUEST, L.get('ui.feedback_sent'), 'toast-info');
                    setTimeout(() => layoutManager.closeOverlay('overlay-feedback'), 1500);
                } else {
                    if (status) { status.textContent = L.get('ui.send_failed').replace('{0}', res.status); status.style.color = 'var(--accent)'; }
                }
            } catch(e) {
                if (status) { status.textContent = L.get('ui.network_error'); status.style.color = 'var(--accent)'; }
            }

            // Always store locally as backup
            try {
                const fb = JSON.parse(localStorage.getItem('PnO_Feedback') || '[]');
                fb.push({ category, subject, description: desc, nickname, time: new Date().toISOString() });
                localStorage.setItem('PnO_Feedback', JSON.stringify(fb));
            } catch(e) {}

            if (document.getElementById('feedback-subject')) document.getElementById('feedback-subject').value = '';
            if (document.getElementById('feedback-description')) document.getElementById('feedback-description').value = '';
        });
    }

    // Quick travel buttons removed — navigation via story choices / location maps

    // Character image overlay controls (0.2.9: the Nude/Clothed tab bar was
    // removed — the eye is the sole dressed⇄nude toggle, play toggles animation).
    // Aktiv-Klasse + Persistenz macht setPanelMode selbst.
    const _eyeBtn = document.getElementById('btn-nude-eye');
    if (_eyeBtn) _eyeBtn.addEventListener('click', () => characterPanelUI.toggleNudeMode());
    const _playBtn = document.getElementById('btn-nude-play');
    if (_playBtn) _playBtn.addEventListener('click', () => characterPanelUI.toggleAnimate());

    // Side-panel tabs (0.2.9: Char / Stats / Hints — left column merged in).
    document.querySelectorAll('.panel-tabs .panel-tab').forEach(btn => {
        btn.addEventListener('click', () => characterPanelUI.setSidePanelTab(btn.dataset.tab));
    });

    // Clickable time slots (advance time on click, matching Unity)
    // Clicking a future time slot advances time, gives +10 Energy per slot,
    // and applies passive arousal/sub gains (P3: matches Unity do_action behavior)
    document.querySelectorAll('.time-seg').forEach(seg => {
        seg.style.cursor = 'pointer';
        seg.addEventListener('click', () => {
            if (!storyManager.currentStory) return;
            const targetSlot = parseInt(seg.dataset.slot);
            const currentSlot = variableManager.getValue('TimeSlot') || 0;
            if (targetSlot > currentSlot) {
                const slotsAdvanced = targetSlot - currentSlot;
                const story = storyManager.currentStory;
                // variableManager.setValue() already writes to Ink (base + buff)
                // and emits STAT_CHANGED. Writing story.variablesState directly
                // beforehand fires a duplicate observer event with the raw value.
                const setVar = (name, val) => {
                    variableManager.setValue(name, val);
                };
                try {
                    // Advance time + update UI
                    setVar('TimeSlot', targetSlot);
                    events.emit(Events.VARIABLE_CHANGED, 'TimeSlot', targetSlot);
                    // Restore energy (+10 per slot, capped at max_energy)
                    const maxEnergy = (story.variablesState['max_energy'] || 100);
                    const curEnergy = variableManager.getValue('Energy') || 0;
                    const newEnergy = Math.min(maxEnergy, curEnergy + slotsAdvanced * 10);
                    setVar('Energy', newEnergy);

                    // P3: Passive stat gains per time slot (matches Unity)
                    for (let i = 0; i < slotsAdvanced; i++) {
                        // Passive arousal (chastity/plug-based)
                        const arousal = variableManager.getValue('Arousal') || 0;
                        const chastity = story.variablesState['wearing_chastity'];
                        const plug = story.variablesState['wearing_plug'];
                        let arousalGain = 0;
                        if (chastity) arousalGain += 2;
                        if (plug) arousalGain += 1;
                        if (arousalGain > 0) {
                            const newArousal = Math.min(100, arousal + arousalGain);
                            setVar('Arousal', newArousal);
                        }

                        // Passive sub gain when arousal >= 50. Route through the
                        // Ink sub_gain() wrapper so the diminishing-returns/tier
                        // curve (get_sub_gain) + 100 clamp apply — a flat JS +1
                        // bypassed all of that. sub_gain mutates the observed
                        // Submissiveness VAR; the inkjs observer mirrors it back
                        // into baseVar.
                        if ((variableManager.getValue('Arousal') || 0) >= 50) {
                            variableManager.evalSafe('sub_gain', [1]);
                        }
                    }
                    // Re-evaluate the current knot's choices so {TimeSlot >= N}
                    // guards on the hub (Downtown's Red Light, Apartment's
                    // sleep, Resort's Night Lounge, etc.) reflect the new
                    // time without requiring the player to navigate away and
                    // back. inkjs caches currentChoices when the knot first
                    // yields them, so a plain refreshView() only re-emits
                    // the FROZEN list to the UI. Re-entering the current
                    // knot via ChoosePathString forces Ink to re-evaluate
                    // the {cond} guards with the new TimeSlot. _locationFamily
                    // suppression in refreshView avoids duplicate narration
                    // cards for same-knot re-entry. Foundby:synaps97 v0.2.6.7
                    // 2026-05-22.
                    try {
                        if (storyManager.currentLocation && story.currentChoices.length > 0) {
                            story.ChoosePathString(storyManager.currentLocation);
                            storyManager.refreshView(false);
                        }
                    } catch(e) { /* non-fatal */ }
                } catch (e) {
                    console.warn('[PnO] Time advance failed:', e);
                }
            }
        });
    });

    // Time-slots toggle (C12, 2026-04-20): at ≤768px the strip collapses
    // to just the active slot + a chevron. Click expands/collapses. The
    // non-active segments use position:fixed (to escape the topbar's
    // overflow:hidden); their anchor is set via CSS vars from the strip's
    // bounding rect at expand time.
    const btnTimeSlotsToggle = document.getElementById('btn-time-slots-toggle');
    const timeSlotsEl = document.getElementById('time-slots');
    if (btnTimeSlotsToggle && timeSlotsEl) {
        btnTimeSlotsToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const willExpand = !timeSlotsEl.classList.contains('expanded');
            if (willExpand) {
                const rect = timeSlotsEl.getBoundingClientRect();
                // Fixed-positioned dropdown anchor: left edge + below the strip.
                // Width matches the strip so MORN-LUNCH-EVE-NIGHT align.
                timeSlotsEl.style.setProperty('--ts-expand-left', `${rect.left}px`);
                timeSlotsEl.style.setProperty('--ts-expand-top', `${rect.bottom + 4}px`);
                timeSlotsEl.style.setProperty('--ts-expand-width', `${rect.width - 12}px`);
            }
            timeSlotsEl.classList.toggle('expanded');
        });
        // Click any slot (inside expanded dropdown) collapses the panel.
        // Also close when clicking anywhere outside.
        timeSlotsEl.querySelectorAll('.time-seg').forEach(seg => {
            seg.addEventListener('click', () => {
                timeSlotsEl.classList.remove('expanded');
            });
        });
        document.addEventListener('click', (e) => {
            if (!timeSlotsEl.contains(e.target)) {
                timeSlotsEl.classList.remove('expanded');
            }
        });
    }

    // Hints panel toggle
    const btnHintsToggle = document.getElementById('btn-hints-toggle');
    const hintsList = document.getElementById('hints-list');
    if (btnHintsToggle && hintsList) {
        btnHintsToggle.addEventListener('click', () => {
            hintsList.classList.toggle('hidden');
            btnHintsToggle.querySelector('svg')?.classList.toggle('rotated');
        });
    }

    // Dynamic hints generation (matches Unity HintsController.GenerateHints)
    events.on(Events.STORY_CHOICES_READY, () => updateHints());
    events.on(Events.STAT_CHANGED, () => updateHints());

    // Character panel footer buttons
    document.querySelectorAll('.char-footer-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const action = btn.dataset.action;
            if (action === 'bugreport') {
                layoutManager.openOverlay('overlay-bugreport');
                window._openBugReportOverlay?.();
            } else if (action === 'feedback') {
                layoutManager.openOverlay('overlay-feedback');
            } else if (action === 'settings') {
                // gleiche Route wie das Topbar-Zahnrad (Pause/Menue-Overlay)
                document.getElementById('btn-settings')?.click();
            }
        });
    });

    // Single gear button in the topbar opens the pause/menu overlay.
    // The redundant hamburger btn-menu was removed in 0.2.3 along with
    // the duplicated overlay-settings.
    document.getElementById('btn-settings')?.addEventListener('click', () => {
        // Each pre-open hook is wrapped so a sync throw in one doesn't block
        // the overlay from opening. VPS report 2026-05-22 Android player at
        // Apartment_Loading: "menu didn't pop up unless I pressed Continue"
        // — most likely cause is one of these hooks throwing because game
        // state is half-initialized at the first knot.
        try { saveLoadUI.refreshSlots(); } catch(e) { console.warn('[Pause] refreshSlots failed:', e); }
        // Refresh the cloud-save badge list on open (non-blocking — badges
        // render from cache immediately, update when the pull resolves).
        try { saveLoadUI.refreshCloudList?.(); } catch(e) { console.warn('[Pause] refreshCloudList failed:', e); }
        try { settingsUI.refreshCloudSaveUI?.(); } catch(e) { console.warn('[Pause] refreshCloudSaveUI failed:', e); }
        try { lovenseSettingsUI.mount(); } catch(e) { console.warn('[Pause] lovense mount failed:', e); }
        try { settingsUI.syncMuteToggles?.(); } catch(e) { console.warn('[Pause] syncMuteToggles failed:', e); }
        // openOverlay('overlay-pause') does NOT emit OVERLAY_OPEN, so the pause
        // content-prefs listener never fires — refresh it manually like the
        // cloud/lovense panels above (0.3 CONTENT_TOGGLES pause surface).
        try { settingsUI.refreshContentPrefsPause?.(); } catch(e) { console.warn('[Pause] content-prefs refresh failed:', e); }
        try { _populateNameFields(); } catch(e) { console.warn('[Pause] name fields failed:', e); }
        layoutManager.openOverlay('overlay-pause');
    });

    // Names tab (Settings/pause) — rename MC (masc+fem), Master/Owner, Girlfriend,
    // Bar manager mid-game (player feedback: "let me change key character names",
    // "accidentally named my GF wrong"). Pre-fill from current Ink state on open.
    function _populateNameFields() {
        const s = storyManager.currentStory;
        if (!s) return;
        const g = (v) => { try { const x = s.variablesState[v]; return (x === undefined || x === null) ? '' : String(x); } catch (e) { return ''; } };
        const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
        set('rename-player', g('PlayerName'));
        set('rename-fem', g('FeminineName'));
        set('rename-master', g('MasterName'));
        set('rename-gf', g('GirlfriendName'));
        set('rename-bar', g('BarManagerName'));
    }
    document.getElementById('btn-rename-save')?.addEventListener('click', () => {
        const s = storyManager.currentStory;
        if (!s) return;
        const clean = (id) => sanitizeName(document.getElementById(id)?.value, 20);
        const player = clean('rename-player') || 'Jamie';
        const fem = clean('rename-fem') || 'Mia';
        const master = clean('rename-master') || 'Alex';
        const gf = clean('rename-gf') || 'Sarah';
        const bar = clean('rename-bar') || 'Marcus';
        // setCharacterNames mirrors baseVar + Ink with observers blocked (no
        // NAME_CHANGED), which is fine in the editor but stale for live UI here —
        // so emit NAME_CHANGED ourselves so phone contacts / header re-render.
        variableManager.setCharacterNames(player, master, gf, fem);
        try {
            variableManager.blockObservers = true;
            try { s.variablesState['BarManagerName'] = bar; } finally { variableManager.blockObservers = false; }
            variableManager.baseVar.set('BarManagerName', bar);
        } catch (e) { console.warn('[Names] BarManagerName write failed:', e?.message); }
        for (const [k, v] of [['PlayerName', player], ['FeminineName', fem], ['MasterName', master], ['GirlfriendName', gf], ['BarManagerName', bar]]) {
            try { events.emit(Events.NAME_CHANGED, k, v); } catch (e) {}
        }
        const fb = document.getElementById('rename-feedback');
        if (fb) { fb.textContent = L.get('names.saved') || 'Saved'; setTimeout(() => { if (fb) fb.textContent = ''; }, 2500); }
        _populateNameFields();  // reflect the sanitized/defaulted values back
    });

    // Per-slot Save/Load buttons
    const _saveCollectors = () => ({
        getStory: () => storyManager.currentStory || null,
        getVariables: () => variableManager.exportState(),
        getInventory: () => inventoryManager.getSerializableState(),
        getCharacterData: () => App.characterData,
        getSMS: () => phoneUI.serialize?.()?.threads || [],
        getStatistics: () => statisticsTracker.getSerializableState(),
        getAchievements: () => achievementManager.getUnlockedIds?.() || [],
        getGalleryUnlocked: () => galleryUI.serialize?.() || [],
        // getCurrentLocation MUST be here (foundby:Ларс 0.2.9). Without it a manual
        // slot save stores currentLocation:'' — on load, step 8b falls back to the
        // Ink path, which is null at a choice point, so currentLocation stays stale
        // and the EMPTY_PANEL_RECOVERY replays the WRONG knot's cached narration.
        // The registered autosave collectors (bootAndApplySave) already include it;
        // this local set for the pause-overlay Save button had drifted out of sync.
        getCurrentLocation: () => storyManager.currentLocation || '',
        gameVersion: VERSION_INFO.fullVersion,
    });

    document.querySelectorAll('.slot-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const action = btn.dataset.action;
            const slot = parseInt(btn.dataset.slot);

            if (action === 'save' && !isNaN(slot) && slot >= 0) {
                // Audit D01: confirm before overwriting a populated slot.
                // confirmOverwrite resolves true for empty slots (no-op).
                const proceed = await saveLoadUI.confirmOverwrite(slot);
                if (!proceed) return;
                await saveSystem.save(_saveCollectors(), slot);
                saveLoadUI.refreshSlots();
                layoutManager.closeOverlay('overlay-pause');
            } else if (action === 'load') {
                let loadData;
                if (slot === -1) {
                    loadData = await saveSystem.loadAutoSave();
                } else {
                    loadData = await saveSystem.load(slot);
                }
                if (loadData) {
                    // Item-DB recovery: if boot-time load failed and DB is empty,
                    // retry before restoring inventory — else restoreFromSave
                    // silently drops every owned tag with "Unknown item tag"
                    // warnings and the next autoSave overwrites the save with
                    // empty inventory. (Audit 2026-05-23.)
                    if (inventoryManager._itemDb.size === 0) {
                        console.warn('[main] Item DB empty before pause-overlay load — recovery fetch');
                        try { await inventoryManager.loadItemDatabase(); } catch(e) {
                            console.error('[main] Item DB recovery failed:', e?.message);
                        }
                    }
                    // Main-menu slot-load has no booted story → full boot+apply
                    // (foundby:VPS [VPS] Broken Saves 0.2.7). In-game (pause)
                    // already has a story → incremental patch.
                    if (!storyManager.currentStory) {
                        await bootAndApplySave(loadData);
                    } else {
                        loadGameIncremental(loadData, _loaderOpts());
                    }
                    layoutManager.closeOverlay('overlay-pause');
                }
            } else if (action === 'export-slot') {
                await saveLoadUI.exportSave(slot);
            } else if (action === 'delete' && !isNaN(slot) && slot >= 0) {
                // Community request (AWolfe 2026-05-31): clear a save slot.
                // confirmDelete resolves false for empty slots (no-op).
                const proceed = await saveLoadUI.confirmDelete(slot);
                if (!proceed) return;
                await saveSystem.deleteSlot(slot);
                // Cloud copy must go too: otherwise the newest-wins boot sync
                // resurrects the deleted save on the next start. Fire-and-
                // forget; deleteRemoteSlot emits the badge/list refresh itself.
                try {
                    if (cloudSaveManager.isEnabled()
                        && cloudSaveManager.hasCode()
                        && cloudSaveManager.allowedSlots().includes(slot)) {
                        cloudSaveManager.deleteRemoteSlot(slot)
                            .catch(e => console.warn('[CloudSave] slot cloud-delete failed:', e?.message));
                    }
                } catch (e) {}
                saveLoadUI.refreshSlots();
                events.emit(Events.TOAST_REQUEST, L.get('save.deleted', 'Save deleted.'), 'toast-success');
                // Keep the pause overlay open so the player sees the now-empty slot.
            }
        });
    });

    // Pause overlay tab switching: Saves / Settings
    document.querySelectorAll('#overlay-pause .pause-tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const target = btn.dataset.tab;
            document.querySelectorAll('#overlay-pause .pause-tab-btn').forEach(b => b.classList.toggle('active', b === btn));
            document.querySelectorAll('#overlay-pause .pause-tab-panel').forEach(p => p.classList.toggle('active', p.dataset.tab === target));
        });
    });

    // Pause overlay secondary actions (export/import/bugreport/quit)
    document.querySelectorAll('.btn-pause-secondary').forEach(btn => {
        btn.addEventListener('click', async () => {
            const action = btn.dataset.action;
            switch (action) {
                case 'export': saveLoadUI.exportSave(); break;
                case 'import': saveLoadUI.importSave(); break;
                case 'bugreport':
                    layoutManager.closeOverlay('overlay-pause');
                    layoutManager.openOverlay('overlay-bugreport');
                    window._openBugReportOverlay?.();
                    break;
                case 'endgame_progress': {
                    // Post-ending runs: route this to the Run Summary overlay
                    // instead of the prerequisites panel — the player already
                    // reached an ending, prerequisites are meaningless.
                    let postEnding = false;
                    let key = '';
                    try {
                        postEnding = !!variableManager._story?.variablesState?.['post_ending'];
                        key = variableManager._story?.variablesState?.['ending_reached'] || '';
                    } catch(e) {}
                    if (postEnding && ENDSCREEN_KEYS.has(key)) {
                        const { endscreen } = await import('./ui/endscreen.js');
                        layoutManager.closeOverlay('overlay-pause');
                        endscreen.show(key);
                        break;
                    }
                    const { endgameProgress } = await import('./ui/endgame-progress.js');
                    const opened = endgameProgress.open();
                    if (opened) {
                        layoutManager.closeOverlay('overlay-pause');
                    } else {
                        const tooEarly = L.get('endgame.progress.too_early');
                        events.emit(Events.TOAST_REQUEST,
                            tooEarly === 'endgame.progress.too_early' ? 'Progress view unlocks on Day 7' : tooEarly,
                            'pno-toast-info');
                    }
                    break;
                }
                case 'quit':
                    layoutManager.closeOverlay('overlay-pause');
                    // Main menu DOM was unmounted on game start — full reload restores it
                    location.reload();
                    break;
            }
        });
    });

    // Image modal close
    const modal = document.getElementById('image-modal');
    if (modal) {
        modal.addEventListener('click', () => modal.classList.add('hidden'));
    }

    // SMS notification click
    const smsNotif = document.getElementById('sms-notification');
    if (smsNotif) {
        smsNotif.addEventListener('click', () => {
            smsNotif.classList.add('hidden');
            layoutManager.openOverlay('overlay-phone');
        });
    }
}

// Save Export/Import — extracted to ui/save-load-ui.js (imported at top)


// Journal Overlay — extracted to ui/journal.js
// Achievement Overlay — extracted to ui/achievements-overlay.js

// ============================================
// Achievement Overlay — data sourced from achievementManager
// ============================================



// History / Story Log — extracted to ui/history.js


// Phone sidebar backdrop handled by layout.js (drawer-open + _addPhoneBackdrop)

// Cheat Console — extracted to ui/cheat-console.js (imported at top)
// Image Vote Service + Game Data Service — extracted to services/analytics.js (imported at top)


// Save Slot Display — extracted to ui/save-load-ui.js (saveLoadUI.refreshSlots)




// Changelog / Version Info — extracted to ui/changelog.js (imported at top)

// Dynamic Hints — extracted to ui/hints.js
// ============================================
// Boot
// ============================================
init().catch(err => {
    console.error('[PnO] Fatal init error:', err);
    // Show error on loading screen so itch.io users can see it
    const pct = document.getElementById('loading-pct');
    if (pct) pct.textContent = 'Failed to load';
    const fill = document.getElementById('loading-fill');
    if (fill) fill.style.background = '#e06060';
    // Actionable recovery card (almost always a CDN/network/blocker 403, not
    // a packaging bug). Inject into the empty label slot below the bar.
    renderLoadFailureCard(document.getElementById('loading-label'), err);
});
// v1774200051
